/** Deals (Phase 5): grouped-by-stage list. When the database's deal door is
 * open (capability probe), each deal gets stage-move arrows and a small
 * editor for the card's money fields — same op semantics as v1's kanban, so
 * v1 sees every change on its next poll. Editing is hidden (not broken)
 * until MIGRATION_CRM2_PHASE5_DEALS.sql runs. Deal CREATION stays in v1 for
 * now (its create-flow also builds Drive folders server-side). */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { moneyExact } from "../../lib/money";
import { DEAL_FIELDS, DEAL_GROUPS, dealContacts, dealSellingComputed, dealTimeline, narrowRelVendors, narrowDealShowings, narrowDealTeam, narrowDealChat, dealShowingId, SHOWING_STATUS, fmtDateEdit, stageName, STAGE_NAMES, toDateInput, daysUntil, countdownLabel, countdownTone, type Deal, type RelVendor, type DealShowing } from "./api";
import { contractorsForDeal } from "../contractors/api";
import { incompleteChecklist, isTaskDone, WF_STAGE_TASKS, type TaskRec } from "../edits/api";
import { natKey as natKeyOf } from "../../lib/natkey";
import { MoneyText } from "../shared/MoneyText";
import { PastContactPicker } from "../contacts/PastContactPicker";
import { PAST_KEYS, type PastContacts } from "../contacts/pastContacts";
import { mergeMaster } from "../roi/math";
import { dealKpis, dealMoneyChips, dealRehabValue } from "../roi/estMoney";
import { arvOverrideOf } from "../edits/api";
import { DriveSection, type DriveLoaders } from "../drive/DriveSection";
import { SowSection } from "../sow/SowSection";
import { parseSharedTemplates } from "../sow/templates";
import { ContractorsSection } from "../contractors/ContractorsSection";
import { TxnExpensesSection } from "../expenses/TxnExpensesSection";
import { SellingSection, type SellingAi } from "../selling/SellingSection";
import { EmailsSection, type MailLoaders } from "../mail/EmailsSection";
import { EsignModal } from "../esign/EsignModal";
import { ContractorAgreementModal } from "../contracts/ContractorAgreementModal";
import { PartnershipMouModal } from "../contracts/PartnershipMouModal";
import { useConfirmDelete } from "../common/ConfirmDelete";
import { grandTotals, type SowState } from "../sow/api";
import type { RecOp } from "../../lib/outbox";
// Inline property tabs (Tom 2026-07-19: "the information should just be there
// without clicking and going to a different page") — the deal drawer renders
// the SAME panels the property drawer uses, against the linked lead record.
import { OverviewSection, PriceHistorySection, TaxSections, TaxHistorySection, ContactDocsCard, usePhone, focusOnMount } from "../leads/LeadDrawer";
import { CompsPanel } from "../comps/CompsPanel";
import { ArvSection } from "../arv/ArvSection";
import { PermitsSection } from "../permits/PermitsSection";
import type { Lead } from "../../types";
import type { CompRow, CompsFilters, CompsSubject, SavedComp } from "../comps/api";
import type { ArvResult } from "../arv/api";
import type { AiPermitResult } from "../permits/api";
import { stFmt, stIcon, type StEvent, type StFeedResult } from "./showingtime";
import { Icon } from "../shell/icons";

/** Per-stage board accent + icon, index-aligned to STAGE_NAMES (v1 TXN_COLS
 * tints: amber → sky → blue → emerald → cyan → violet → green). */
const STAGE_CLASS = ["s-contract", "s-closed", "s-rehab", "s-market", "s-inspect", "s-pending", "s-sold"];
const STAGE_ICON = ["📝", "🔑", "🔨", "🏷️", "🔍", "⏳", "✅"];
/** v1 board card shows the first two comma-parts of the address. */
const shortAddr = (a: string) => a.split(",").slice(0, 2).join(",").trim();

/** Synthetic property subject for a deal with NO scraped lead record (manual
 * deals) — v1's AI tab built the same (`{a:addr, sqft:edits.pp_sqft, beds:null}`)
 * so the comps/ARV/permits engines still run; sections render their empty
 * states. All persistence is address-keyed, so edits land in LEAD_EDITS and
 * v1 sees them exactly as if made from the property drawer. */
function dealAsLead(deal: Deal, leadEdits: Record<string, unknown> | undefined): Lead {
  const nOf = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const f = typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(f) ? f : null;
  };
  const sOf = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);
  const pick = (...keys: string[]): unknown => {
    for (const k of keys) {
      const dv = deal.fields[k];
      if (dv !== null && dv !== undefined && dv !== "") return dv;
      const lv = leadEdits?.[k];
      if (lv !== null && lv !== undefined && lv !== "") return lv;
    }
    return null;
  };
  return {
    id: "deal:" + deal.natKey,
    createdAt: 0,
    address: deal.address,
    source: "Deal",
    price: nOf(pick("pp_purch", "t_purchase", "pp_ask")),
    arv: nOf(pick("pp_carv", "pp_larv")),
    arvPct: null,
    county: sOf(pick("pp_county", "county")),
    beds: nOf(pick("pp_beds", "beds")),
    baths: nOf(pick("pp_baths", "baths")),
    sqft: nOf(pick("pp_sqft", "sqft", "countySqft")),
    zest: null,
    rentZest: null,
    cdom: null,
    seller: null,
    marketStatus: null,
    priceDrop: null,
    reductions: 0,
    priceDrops: [],
    priceHistory: [],
    photos: [],
    desc: null,
    taxHistory: [],
    yearBuilt: nOf(pick("pp_yr", "yearBuilt", "year")),
    permits: [],
    permitsChecked: false,
    permitUrl: null,
  };
}
/** v1 TXN_TABS — the 19 transaction-detail tabs, in order, with an icon each
 * (v1 used lucide; emoji here) and which ones carry a live count badge. */
const DEAL_TABS: { id: string; label: string; icon: string; count?: "showings" | "checklist" | "expenses" }[] = [
  { id: "purchasing", label: "Purchasing", icon: "🛒" },
  { id: "rehab", label: "Rehab", icon: "🔧" },
  { id: "selling", label: "Selling", icon: "🏷️" },
  { id: "expenses", label: "Expenses", icon: "💲", count: "expenses" },
  { id: "property", label: "Property", icon: "🏠" },
  { id: "comps", label: "Comps", icon: "📊" },
  { id: "listing", label: "Listing", icon: "📋" },
  { id: "tax", label: "Tax", icon: "🏛️" },
  { id: "history", label: "History", icon: "🕑" },
  { id: "showings", label: "Showings", icon: "👁️", count: "showings" },
  { id: "docs", label: "Drive", icon: "📁" },
  { id: "activity", label: "Activity", icon: "📈" },
  { id: "team", label: "Team", icon: "👥" },
  { id: "permits", label: "Permits", icon: "📋" },
  { id: "checklist", label: "Checklists", icon: "☑️", count: "checklist" },
  { id: "ai", label: "AI Estimator", icon: "✨" },
  { id: "insurance", label: "Insurance", icon: "🛡️" },
  { id: "renovai", label: "RenovAI", icon: "🪄" },
  { id: "emails", label: "Emails", icon: "✉️" },
];
/** whole-day difference a→b (v1 daysDiff), local midnight-anchored. */
const daysBetween = (a: Date, b: Date): number =>
  Math.round((new Date(b).setHours(0, 0, 0, 0) - new Date(a).setHours(0, 0, 0, 0)) / 86400000);
/** v1 DEAL_LABEL_C → dark-theme pill class suffix. */
const LABEL_CLASS: Record<string, string> = { Flip: "flip", Rental: "rental", BRRRR: "brrrr", Wholesale: "whole", Land: "land" };
/** v1 Object.keys(DEAL_LABEL_C) — the five deal labels, in order. */
const DEAL_LABELS = ["Flip", "Rental", "BRRRR", "Wholesale", "Land"] as const;
/** v1 ASSIGN_COLORS — the eight assignment swatch colors, in order. */
const ASSIGN_COLORS = ["violet", "blue", "emerald", "amber", "red", "cyan", "indigo", "pink"] as const;
/** Swatch/pill hue per ASSIGN_COLORS entry (v1 used Tailwind bg-{color}-500). */
const ASSIGN_HUES: Record<string, string> = {
  violet: "#8b5cf6", blue: "#3b82f6", emerald: "#10b981", amber: "#f59e0b",
  red: "#ef4444", cyan: "#06b6d4", indigo: "#6366f1", pink: "#ec4899",
};
/** v1 types = [['Unassigned','x'],['Investor','building-2'],['Partner','user']]. */
const ASSIGN_TYPES: { t: string; ic: string }[] = [
  { t: "Unassigned", ic: "∅" }, { t: "Investor", ic: "🏢" }, { t: "Partner", ic: "🤝" },
];

/** Board cards show the assigned crew from Closed through Pending Sale —
 * Tom (2026-07-28): "from Closed stage until Sold (sold not included)".
 * Derived from STAGE_NAMES rather than hardcoded so inserting a stage doesn't
 * silently shift the range; the fallbacks keep the range sane if a name moves. */
const CREW_FROM_STAGE = STAGE_NAMES.indexOf("Closed") < 0 ? 1 : STAGE_NAMES.indexOf("Closed");
const CREW_TO_STAGE = (STAGE_NAMES.indexOf("Sold") < 0 ? STAGE_NAMES.length : STAGE_NAMES.indexOf("Sold")) - 1;

export function DealsPage({
  deals,
  contacts,
  pastContacts,
  tasks,
  ready,
  note,
  canEdit,
  canEditProbing = false,
  orgSettings,
  edits,
  members = [],
  onMoveStage,
  onEditField,
  onToggleTask,
  onAddTask,
  stageBlock,
  onDismissBlock,
  onOpenTasks,
  driveLoaders,
  onLabelDriveFile,
  mailLoaders,
  fetchSow,
  onSowOps,
  onSowForceSave,
  materials,
  onNeedMaterials,
  onPutMaterial,
  materialCats = [],
  onImportUrl,
  onSaveContractors,
  onSaveTxnExpenses,
  onSaveScopeTpls,
  onSaveDealBlob,
  onSaveAssign,
  onDeleteDeal,
  sellingAi,
  onOpenProperty,
  me = "T",
  onPostContractorExpense,
  jumpKey,
  onJumped,
  token = null,
  isAdmin = false,
  onOpenChange,
  leads,
  onLeadField,
  onCompsPersist,
  onCompsArv,
  compsSearch,
  compsPhotos,
  onRunArv,
  onSaveArvResult,
  onAiPermitSearch,
  initialTab = null,
  onTabChange,
  initialSub = null,
  onSubChange,
  onShowingFeed,
  uiPrefs = {},
  onSaveUiPrefs,
}: {
  deals: Deal[];
  /** all contacts (v1 Add-Vendor "Choose from contacts" dropdown). */
  contacts: import("../contacts/api").Contact[];
  /** past title/dispo contacts for the one-pick fill (2026-08-31) */
  pastContacts?: PastContacts | undefined;
  tasks: TaskRec[];
  ready: boolean;
  note: string | null;
  canEdit: boolean;
  /** true while the deal write-probe is still in flight. The UI must say
   *  "checking" rather than offer an enabled control wired to a no-op —
   *  every write below is swapped for noEdit until canEdit is true. */
  canEditProbing?: boolean;
  orgSettings: Record<string, unknown>;
  edits: Map<string, Record<string, unknown>>;
  /** team display names — the assignee picker on the deal's task adder. Names
   * must match the profiles roster or the server can't resolve who to notify. */
  members?: string[];
  onMoveStage: (natKey: string, stage: number) => void;
  onEditField: (natKey: string, field: string, value: string | null) => void;
  onToggleTask: (key: string, done: boolean) => void;
  onAddTask: (title: string, opts?: { deal?: string; due?: string; who?: string; pri?: string }) => void;
  stageBlock: { deal: string; stage: string; titles: string[] } | null;
  onDismissBlock: () => void;
  onOpenTasks: () => void;
  driveLoaders: DriveLoaders;
  /** CRM-side name for a Drive file (never renames the Drive file) */
  onLabelDriveFile?: (natKey: string, fileId: string, label: string | null) => void;
  /** v1 leadMailPanel — seller/agent email thread, shown inline on the deal's
   * Emails tab (address-keyed, same backend as the lead drawer). */
  mailLoaders: MailLoaders;
  fetchSow: (dealKey: string) => Promise<SowState | null>;
  onSowOps: (ops: RecOp[]) => void;
  /** SOW Save button: flush the outbox, resolve true when nothing is left pending. */
  onSowForceSave: () => Promise<boolean>;
  materials: import("../materials/api").CatalogMaterial[];
  onNeedMaterials: () => void;
  /** create a catalog material inline from the SOW item editor (edit-gated) */
  onPutMaterial?: (id: string, input: import("../materials/api").MaterialInput, keepHistory: { date: string; price: number }[]) => void;
  /** catalog categories + product-link import for the inline material editor */
  materialCats?: import("../materials/api").MaterialCat[];
  onImportUrl?: (url: string, onWake: () => void) => Promise<import("../materials/api").MaterialPriceResult>;
  onSaveContractors: (all: Record<string, unknown>[]) => void;
  onSaveTxnExpenses: (all: Record<string, unknown>[]) => boolean | void;
  /** Phase 28: persist the whole org SCOPE_TPLS array (v1 shape, ledger write). */
  onSaveScopeTpls?: (all: unknown[]) => void;
  onSaveDealBlob: (natKey: string, key: string, value: unknown) => void;
  /** v1 saveAssign — persist the deal's Internal Assignment (label + partner). */
  onSaveAssign: (natKey: string, label: string | null, partner: { type: string; name: string; color: string; note: string } | null) => void;
  /** v1 deleteTxnCard — soft-delete the deal (recoverable). */
  onDeleteDeal: (natKey: string) => void;
  sellingAi: SellingAi;
  /** jump to the linked property (lead drawer: Photos, Comps, ARV, Emails, Permits) */
  onOpenProperty: (address: string) => void;
  me?: string; // current user initial for deal team chat
  onPostContractorExpense?: (address: string, exp: { desc: string; amount: number; method: string; vendor: string; date: string }) => void;
  jumpKey?: string | null; // open this deal on mount (from the Utilities page)
  onJumped?: () => void;
  token?: string | null; // Bearer token for Send-for-Signature (BoldSign)
  /** admin may save the contractor-agreement defaults (owner, lender, split) */
  isAdmin?: boolean;
  onOpenChange?: (natKey: string | null) => void; // report the open deal (view persistence)
  /** all property records — the deal's Comps/Listing/Tax/History/Permits/AI
   * tabs render inline against the matching lead (LeadDrawer panels, reused). */
  leads: Lead[];
  /** the LEAD_EDITS writer (address-keyed, shared with v1) — Tax + AI fields. */
  onLeadField: (address: string, key: string, value: string | null) => void;
  onCompsPersist: (address: string, state: { filters: CompsFilters; saved: Record<string, SavedComp> }) => void;
  onCompsArv: (address: string, arv: number) => void;
  compsSearch: (address: string, f: CompsFilters, onWake: () => void) => Promise<{ comps: CompRow[]; subject: CompsSubject | null }>;
  compsPhotos: (comp: CompRow) => Promise<string[]>;
  onRunArv: (address: string, sqft: number, beds: number | null, onWake: () => void) => Promise<ArvResult>;
  onSaveArvResult: (address: string, result: ArvResult) => void;
  onAiPermitSearch: (address: string, county: string, leadId: string) => Promise<AiPermitResult>;
  /** restore the drawer's inner tab after a refresh (v1 _TT0 parity) */
  initialTab?: string | null;
  onTabChange?: (tab: string) => void;
  /** restore the Rehab tab's inner sub-tab (details|sow|photos) after a refresh */
  initialSub?: string | null;
  onSubChange?: (sub: string) => void;
  /** v1 stFetch — ShowingTime feed events for one address (Showings tab) */
  onShowingFeed?: (feedUrl: string, address: string) => Promise<StFeedResult>;
  /** per-user interface prefs — which deal tabs this person pinned */
  uiPrefs?: import("../settings/uiPrefs").UiPrefs;
  onSaveUiPrefs?: (next: import("../settings/uiPrefs").UiPrefs) => void;
}) {
  const [q, setQ] = useState("");
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [view, setView] = useState<"board" | "list">("board");
  // header actions: Send-for-Signature (BoldSign) modal + typed-delete (v1)
  const [esignOn, setEsignOn] = useState(false);
  // Contractor agreement generator (Tom 2026-09-04) — opened from the Expenses tab
  const [agreementOn, setAgreementOn] = useState(false);
  // Partnership MOU generator (Tom 2026-09-10) — bottom of the Purchasing tab
  const [mouOn, setMouOn] = useState(false);
  const confirmDeal = useConfirmDelete();
  const [dtab, setDtabRaw] = useState<string>(() =>
    initialTab && DEAL_TABS.some((t) => t.id === initialTab) ? initialTab : "purchasing",
  );
  // every tab change is reported up so a refresh restores the same tab
  const setDtab = (t: string) => { setDtabRaw(t); onTabChange?.(t); };
  // v1 Inside-Rehab / Inside-Selling sub-tab selectors.
  const [rsub, setRsubRaw] = useState<string>(
    initialSub === "details" || initialSub === "sow" || initialSub === "photos" ? initialSub : "details",
  );
  // report sub-tab changes up so a refresh restores SOW as SOW (not Details)
  const setRsub = (s: string) => { setRsubRaw(s); onSubChange?.(s); };
  const [ssub, setSsub] = useState<string>("details");
  // v1 openAssignModal — the ⋯ Internal Assignment modal, keyed by deal natKey.
  const [assignKey, setAssignKey] = useState<string | null>(null);
  const [pinOpen, setPinOpen] = useState(false);
  /** true once the tab strip is scrolled to its end — the right-edge fade turns
   *  off there, because a fade over the last tab implies more that isn't. */
  const [tabsAtEnd, setTabsAtEnd] = useState(false);
  /** Phone layout. Live, not one-shot: rotating the device has to move the
   *  header back, or the desktop stats would stay hidden in landscape. */
  const [isPhone, setIsPhone] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 720px)");
    const on = () => setIsPhone(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  /** Pinned deal tabs ride at the front of the strip; everything else keeps
   *  DEAL_TABS order so nothing shuffles under the user's thumb. */
  const pinnedTabs = uiPrefs.dealTabs ?? [];
  const orderedDealTabs = useMemo(() => {
    const pinnedSet = new Set(pinnedTabs);
    const pinned = pinnedTabs
      .map((id) => DEAL_TABS.find((t) => t.id === id))
      .filter((t): t is (typeof DEAL_TABS)[number] => Boolean(t));
    return [...pinned, ...DEAL_TABS.filter((t) => !pinnedSet.has(t.id))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedTabs.join("|")]);
  const toggleDealPin = (id: string) => {
    const next = pinnedTabs.includes(id) ? pinnedTabs.filter((p) => p !== id) : [...pinnedTabs, id];
    onSaveUiPrefs?.({ ...uiPrefs, dealTabs: next });
  };

  // Deals write-gate closed (MIGRATION_CRM2_PHASE5_DEALS.sql not run): the drawer
  // still OPENS so details are viewable, but every write is swapped for a no-op
  // — the app-level deal handlers aren't gated, so this is what keeps it truly
  // read-only (no ops enqueued) instead of a dead card.
  const noEdit = (): void => {};
  const roEditField = canEdit ? onEditField : noEdit;
  const roMoveStage = canEdit ? onMoveStage : noEdit;
  const roSaveBlob = canEdit ? onSaveDealBlob : noEdit;
  const roSowOps = canEdit ? onSowOps : noEdit;
  const roNeedMaterials = canEdit ? onNeedMaterials : noEdit;
  const roSaveContractors = canEdit ? onSaveContractors : noEdit;
  const roSaveTxnExpenses = canEdit ? onSaveTxnExpenses : noEdit;
  const roToggleTask = canEdit ? onToggleTask : noEdit;
  const roAddTask = canEdit ? onAddTask : noEdit;
  const roPostExpense = canEdit ? onPostContractorExpense : undefined;
  const roDeleteDeal = canEdit ? onDeleteDeal : noEdit;

  const filteredDeals = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? deals.filter((d) => d.address.toLowerCase().includes(needle)) : deals;
  }, [deals, q]);

  // Board view: one column per stage (all stages shown, even empty ones, so the
  // whole pipeline is visible and cards can move into empty stages).
  const board = useMemo(
    () => STAGE_NAMES.map((_, i) => filteredDeals.filter((d) => d.stageIdx === i)),
    [filteredDeals],
  );

  // List view: only stages that have deals, in order (the original grouped list).
  const groups = useMemo(() => {
    const byStage = new Map<number, Deal[]>();
    for (const d of filteredDeals) {
      const arr = byStage.get(d.stageIdx);
      if (arr) arr.push(d);
      else byStage.set(d.stageIdx, [d]);
    }
    return [...byStage.entries()].sort((a, b) => a[0] - b[0]);
  }, [filteredDeals]);

  const open = openKey ? deals.find((d) => d.natKey === openKey) ?? null : null;
  // The header's REHAB figure prefers the deal's live SOW total (the number Tom
  // actually manages) over the card/property estimate — fetched once per opened
  // deal; null = no scope yet, fall back to the estimate chain.
  const [sowTotal, setSowTotal] = useState<number | null>(null);
  useEffect(() => {
    setSowTotal(null);
    if (!openKey) return;
    let alive = true;
    void fetchSow(openKey).then((s) => {
      if (!alive || !s) return;
      const t = grandTotals(s).total;
      if (t > 0) setSowTotal(t); // exact — never round money (Tom 2026-07-24)
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  // ---- inline property tabs (Tom 2026-07-19) -------------------------------
  // The linked lead record, exact natKey first, street-segment fallback (deal
  // addresses sometimes carry different city/zip formatting than the scrape).
  const propLead = useMemo(() => {
    if (!open) return null;
    const want = natKeyOf(open.address);
    const exact = leads.find((l) => natKeyOf(l.address) === want);
    if (exact) return exact;
    const street = (want.split(",")[0] ?? "").trim();
    return street ? leads.find((l) => (natKeyOf(l.address).split(",")[0] ?? "").trim() === street) ?? null : null;
  }, [leads, open]);
  // Always render the property tabs inline: real lead when one matches, else a
  // synthetic subject built from the deal's own fields (v1 did the same for its
  // AI tab — `{a:addr, sqft:edits.pp_sqft, ...}`); comps/ARV/permits/tax all
  // key their persistence by ADDRESS, so writes work either way.
  const propSubject = useMemo<Lead | null>(() => {
    if (!open) return null;
    if (propLead) return propLead;
    return dealAsLead(open, edits.get(natKeyOf(open.address)));
  }, [open, propLead, edits]);
  const propFields = open ? edits.get(natKeyOf(open.address)) : undefined;

  // v1 _cardTaskStats: done/total over ALL tasks whose deal matches the card
  const taskStats = useMemo(() => {
    const m = new Map<string, { d: number; t: number }>();
    for (const t of tasks) {
      if (!t.deal) continue;
      const k = natKeyOf(t.deal);
      const s = m.get(k) ?? { d: 0, t: 0 };
      s.t++;
      if (isTaskDone(t.status)) s.d++;
      m.set(k, s);
    }
    return m;
  }, [tasks]);

  /** Assigned crew per deal, for the stages that show it. TXN_CONTRACTORS is
   * ONE array across every deal, so resolving it inside the card renderer
   * meant re-scanning the whole blob per card per render — the same trap the
   * MOBILE PERF note above warns about. Computed once per deals/settings
   * change, and it goes through contractorsForDeal so the full-address /
   * street-key fallback stays in exactly one place. */
  const crewByDeal = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const d of deals) {
      if (d.stageIdx < CREW_FROM_STAGE || d.stageIdx > CREW_TO_STAGE) continue;
      const names = [...new Set(contractorsForDeal(orgSettings, d.address).map((c) => c.name.trim()).filter(Boolean))];
      if (names.length) m.set(d.natKey, names);
    }
    return m;
  }, [deals, orgSettings]);

  // parity with the lead drawer: Esc closes — but not while the e-sign modal
  // (which owns its own Esc) or the assign modal is open on top.
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape" && !esignOn && !assignKey) setOpenKey(null); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, esignOn, assignKey]);

  // closing the deal clears any open header action (e-sign)
  useEffect(() => { if (!open) { setEsignOn(false); setAgreementOn(false); setMouOn(false); } }, [open]);

  // report the open deal so a refresh can reopen it (view persistence)
  useEffect(() => { onOpenChange?.(openKey); }, [openKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // open a specific deal on request (v1 utilOpenDeal — the Utilities page jumps here)
  useEffect(() => {
    if (!jumpKey) return;
    setOpenKey(jumpKey);
    onJumped?.();
  }, [jumpKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // org-wide SOW templates from v1's SCOPE_TPLS (read-only, apply-only in v2).
  // MUST stay above the `deals.length === 0` early-return below — otherwise the
  // hook count changes when deals load in (empty → populated on saved-view
  // restore), which throws React #310 and white-screens the app.
  const sharedSowTemplates = useMemo(() => parseSharedTemplates(orgSettings["SCOPE_TPLS"]), [orgSettings]);

  if (deals.length === 0) {
    return (
      <div className="deals">
        <div className="empty gate">
          <strong>No deals to show here yet.</strong>
          <span>
            Your deals live in v1 and are safe. If you have deals there and expect them here,
            v2 needs read access — run <code>MIGRATION_CRM2_PHASE4_READS.sql</code> in Supabase
            (read-only; it can't change anything).
          </span>
        </div>
      </div>
    );
  }
  void ready;

  const money = (f: Record<string, unknown>, k: string): string | null =>
    typeof f[k] === "string" && (f[k] as string).trim() ? (f[k] as string) : null;

  // v1 txnCard Est-Money chips (Contract Signed: both; Closed: after only)
  const nrMaster = mergeMaster(orgSettings["NR_MASTER"]);
  const nrBy = orgSettings["NR_BY"];
  const chipsFor = (d: Deal) => {
    // v1: rh = money(c.rehab) || leadRehabValue(L) — card rehab wins
    const rehab = d.rehab || dealRehabValue(d.address, nrBy, edits.get(natKeyOf(d.address)));
    return dealMoneyChips(d.stageIdx, d.fields, rehab, nrMaster);
  };

  // v1 txnCard, ported to v2's data (address, label/partner, purchase, est-money
  // chips, task stats, stage-move arrows). Used by the Kanban board columns.
  const dealCard = (d: Deal, stage: number) => {
    const purchase = money(d.fields, "t_purchase");
    const ch = chipsFor(d);
    const s = taskStats.get(d.natKey) ?? taskStats.get(natKeyOf(d.address));
    const labelCls = d.label ? LABEL_CLASS[d.label] ?? "flip" : null;
    // Tom (2026-07-28): a date you have to open the card to see is a date you
    // miss. Both countdowns render on the card whenever the field is filled.
    //
    // EXCEPT the closing countdown once the deal has actually closed
    // (2026-07-29). "🔑 Closing yesterday" on a card sitting in Closed is
    // counting down to something that already happened — it reads like an
    // overdue action when there is nothing left to do, and the red tone makes
    // it look urgent. Only stages BEFORE Closed have a closing still ahead of
    // them; from Closed onward the date belongs in the deal's detail, not as a
    // countdown chip. The closing DATE is still on the card's stat row.
    //
    // Post-occupancy deliberately keeps its countdown here: it starts AT
    // closing and runs afterwards, so it is still live in Closed and Rehab —
    // which is exactly when you need to see it.
    const closedIdx = STAGE_NAMES.indexOf("Closed");
    const beforeClosing = closedIdx < 0 || stage < closedIdx;
    const closeIn = beforeClosing ? daysUntil(d.fields["t_closing"]) : null;
    const postIn = daysUntil(d.fields["t_postocc"]);
    // Contractors: Closed through Pending Sale. Not Contract Signed (nobody is
    // hired yet) and not Sold (the job is over) — Tom's exact range, applied
    // when crewByDeal was built.
    const crew = crewByDeal.get(d.natKey) ?? [];
    // Lockbox on the card for Rehab / On Market / Buyer Inspection / Pending
    // Sale (Tom 2026-07-30, Rehab added same day) — the stages where someone
    // is standing at the door. Source order is his: the Rehab tab's Property
    // Access FAQ code first, then Purchasing's, and an explicit "empty" if
    // both are blank — a blank would read as "no lockbox on this house",
    // which is a different claim.
    const lockboxStage = ["Rehab", "On Market", "Buyer Inspection", "Pending Sale"].includes(stageName(stage));
    const lockOf = (k: string): string | null => {
      const v = d.fields[k];
      return typeof v === "string" && v.trim() ? v.trim() : null;
    };
    const lockbox = lockOf("r_lockbox") ?? lockOf("t_lockbox");
    return (
      <div
        key={d.natKey}
        className={"kbcard" + " click"}
        onClick={() => setOpenKey(d.natKey)}
      >
        <div className="kbtop">
          <div className="kbaddr">{shortAddr(d.address)}</div>
          <button
            className="kbdots"
            title="Assign / label"
            onClick={(e) => { e.stopPropagation(); setAssignKey(d.natKey); }}
          >⋯</button>
        </div>
        {d.label || d.partner ? (
          <div className="kbchips">
            {labelCls ? <span className={"kbpill " + labelCls}>{d.label}</span> : null}
            {d.partner ? (
              <span className="kbpill partner dot" style={d.partnerColor ? { color: ASSIGN_HUES[d.partnerColor] ?? undefined } : undefined}>{d.partner}</span>
            ) : null}
          </div>
        ) : null}
        <div className="kbppr">Purchase Price: <b>{purchase ?? "—"}</b></div>
        {closeIn !== null || postIn !== null ? (
          <div className="kbdates">
            {closeIn !== null ? (
              <span className={"kbdate " + countdownTone(closeIn)} title={"Closing Date: " + String(d.fields["t_closing"])}>
                🔑 Closing <b>{countdownLabel(closeIn)}</b>
              </span>
            ) : null}
            {postIn !== null ? (
              <span className={"kbdate " + countdownTone(postIn)} title={"Post Occupancy End Date: " + String(d.fields["t_postocc"])}>
                🏠 Post-occ ends <b>{countdownLabel(postIn)}</b>
              </span>
            ) : null}
          </div>
        ) : null}
        {lockboxStage ? (
          <div
            className="kblockbox"
            title={lockbox
              ? "Lockbox code (from " + (lockOf("r_lockbox") ? "Property Access FAQ" : "Purchasing") + ")"
              : "No lockbox code on Property Access FAQ or Purchasing"}
          >
            🔒 Lockbox: {lockbox ? <b>{lockbox}</b> : <span className="none">empty</span>}
          </div>
        ) : null}
        {crew.length ? (
          <div className="kbcrew" title={"Contractors on this deal: " + crew.join(", ")}>
            <span className="l">🔨</span>
            {crew.map((n) => <span key={n} className="kbcrewname">{n}</span>)}
          </div>
        ) : null}
        {ch.closing ? (
          <div className="kbest">
            <span className="l">Est. Money for Closing</span>
            <span className="v">${ch.closing.amount.toLocaleString()} <em>({ch.closing.pct}% {ch.closing.approved ? "Approved" : "Pre-Approval"})</em></span>
          </div>
        ) : null}
        {ch.after ? (
          <div className="kbest after">
            <span className="l">Est. Money After Closing</span>
            <span className="v">${ch.after.amount.toLocaleString()}</span>
          </div>
        ) : null}
        <div className="kbfoot">
          {s && s.t > 0 ? (
            <span className={"kbtasks" + (s.d === s.t ? " done" : "")}>🕑 {s.d}/{s.t} tasks</span>
          ) : (
            <span className="kbupd">{d.updatedAt ? "updated " + new Date(d.updatedAt).toLocaleDateString([], { month: "short", day: "numeric" }) : "—"}</span>
          )}
          {canEdit ? (
            <span className="kbmove" onClick={(e) => e.stopPropagation()}>
              <button
                title={stage > 0 ? `Move to ${stageName(stage - 1)}` : ""}
                disabled={stage <= 0}
                onClick={() => onMoveStage(d.natKey, stage - 1)}
              >←</button>
              <button
                title={stage < STAGE_NAMES.length - 1 ? `Move to ${stageName(stage + 1)}` : ""}
                disabled={stage >= STAGE_NAMES.length - 1}
                onClick={() => onMoveStage(d.natKey, stage + 1)}
              >→</button>
            </span>
          ) : null}
        </div>
      </div>
    );
  };

  const assignModal = assignKey ? (() => {
    const d = deals.find((x) => x.natKey === assignKey);
    if (!d) return null;
    return (
      <AssignModal
        deal={d}
        canEdit={canEdit}
        onClose={() => setAssignKey(null)}
        onSave={(label, partner) => { onSaveAssign(d.natKey, label, partner); setAssignKey(null); }}
        onDelete={() => { roDeleteDeal(d.natKey); setAssignKey(null); if (openKey === d.natKey) setOpenKey(null); }}
      />
    );
  })() : null;

  return (
    <div className="deals">
      {!open && (<>
      <header className="bar">
        <input placeholder="Search deals by address…" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="count">
          {deals.length.toLocaleString()} deals
          {note ? <em className="note"> · {note}</em> : null}
          {!canEdit ? <em className="note"> · read-only (viewable) — editing unlocks when MIGRATION_CRM2_PHASE5_DEALS.sql runs</em> : null}
        </span>
        <div className="kbtoggle" role="tablist" aria-label="Deals view">
          <button className={view === "board" ? "on" : ""} onClick={() => setView("board")} title="Kanban board by stage">▦ Board</button>
          <button className={view === "list" ? "on" : ""} onClick={() => setView("list")} title="Grouped list">☰ List</button>
        </div>
      </header>
      {stageBlock ? (
        <div className="stageblock">
          <strong>Can't advance yet</strong>
          <span>
            Finish the <b>{stageBlock.stage}</b> checklist for {stageBlock.deal} first:{" "}
            {stageBlock.titles.slice(0, 3).join(" · ")}
            {stageBlock.titles.length > 3 ? ` · +${stageBlock.titles.length - 3} more` : ""}
          </span>
          <span className="sbactions">
            <button className="btn slim" onClick={onOpenTasks}>Open in Tasks →</button>
            <button className="vtab" onClick={onDismissBlock}>Dismiss</button>
          </span>
        </div>
      ) : null}
      {view === "board" ? (
        filteredDeals.length === 0 ? (
          <div className="empty">No deals match.</div>
        ) : (
          <div className="kbboard">
            {STAGE_NAMES.map((name, i) => {
              const cards = board[i] ?? [];
              return (
                <div key={i} className={"kbcol " + (STAGE_CLASS[i] ?? "")}>
                  <div className="kbcolhd">
                    <span className="ic">{STAGE_ICON[i] ?? ""}</span>
                    <span className="nm">{name}</span>
                    <span className="ct">{cards.length}</span>
                  </div>
                  <div className="kbcards">
                    {cards.length === 0 ? (
                      <div className="kbempty">No deals</div>
                    ) : (
                      cards.map((d) => dealCard(d, i))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
      <div className="deallist">
        {groups.length === 0 ? <div className="empty">No deals match.</div> : null}
        {groups.map(([stage, ds]) => (
          <div key={stage} className="dealgroup">
            <div className="stagehdr">
              {stageName(stage)} <span className="stagecount">{ds.length}</span>
            </div>
            {ds.map((d) => (
              <div key={d.natKey} className={"dealrow" + " click"} onClick={() => setOpenKey(d.natKey)}>
                <div className="dealmain">
                  <strong>
                    {d.address}
                    {d.label ? <span className={"kbpill " + (LABEL_CLASS[d.label] ?? "flip")}>{d.label}</span> : null}
                    {d.partner ? <span className="kbpill partner dot" style={d.partnerColor ? { color: ASSIGN_HUES[d.partnerColor] ?? undefined } : undefined}>{d.partner}</span> : null}
                  </strong>
                  <span className="sub">
                    {money(d.fields, "t_purchase") ? `purchase ${money(d.fields, "t_purchase")} · ` : ""}
                    {d.updatedAt ? "updated " + new Date(d.updatedAt).toLocaleDateString([], { month: "short", day: "numeric" }) : "—"}
                  </span>
                  {(() => {
                    const ch = chipsFor(d);
                    return ch.closing || ch.after ? (
                      <span className="estchips">
                        {ch.closing ? (
                          <span className="estchip closing">
                            Est. Money Needed for Closing <strong>${ch.closing.amount.toLocaleString()}</strong>
                            <em>({ch.closing.pct}% funded {ch.closing.approved ? "Approved" : "Pre-Approval"})</em>
                          </span>
                        ) : null}
                        {ch.after ? (
                          <span className="estchip after">
                            Est. Money Needed After Closing <strong>${ch.after.amount.toLocaleString()}</strong>
                          </span>
                        ) : null}
                      </span>
                    ) : null;
                  })()}
                </div>
                {(() => {
                  const s = taskStats.get(d.natKey) ?? taskStats.get(natKeyOf(d.address));
                  return s && s.t > 0 ? (
                    <span className={"lbadge " + (s.d === s.t ? "bon" : "bdup")} title={`${s.d} of ${s.t} tasks done`}>
                      {s.d}/{s.t}
                    </span>
                  ) : null;
                })()}
                {canEdit ? (
                  <div className="stagemove" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="stagebtn"
                      title={stage > 0 ? `Move to ${stageName(stage - 1)}` : ""}
                      disabled={stage <= 0}
                      onClick={() => onMoveStage(d.natKey, stage - 1)}
                    >
                      ←
                    </button>
                    <button
                      className="stagebtn"
                      title={stage < STAGE_NAMES.length - 1 ? `Move to ${stageName(stage + 1)}` : ""}
                      disabled={stage >= STAGE_NAMES.length - 1}
                      onClick={() => onMoveStage(d.natKey, stage + 1)}
                    >
                      →
                    </button>
                  </div>
                ) : null}
                <button
                  className="kbdots row"
                  title="Assign / label"
                  onClick={(e) => { e.stopPropagation(); setAssignKey(d.natKey); }}
                >⋯</button>
              </div>
            ))}
          </div>
        ))}
      </div>
      )}
      </>)}
      {open ? (
        <div className="dtxn">
          <button className="txnback" onClick={() => setOpenKey(null)}>← Back to Transactions</button>

          {(() => {
            // v1 txnDetailView header: address + stage + Project Lifetime, the
            // stat row ($ Purchase / ARV / Rehab / Proj ROI / Closing / Days to
            // Closing), the Team avatars, and the Send-for-Signature / Delete
            // actions.
            // Rehab: the deal's SOW total when one exists (that's the managed
            // number — Tom: "$50k was the Properties estimate"), else the
            // card/property estimate chain as before. ROI uses the same figure.
            const rehab = sowTotal ?? (open.rehab || dealRehabValue(open.address, nrBy, edits.get(natKeyOf(open.address))));
            const leadArv = arvOverrideOf(edits.get(natKeyOf(open.address)));
            const k = dealKpis(open.fields, rehab, leadArv, nrMaster);
            const arvTxt = money(open.fields, "t_arv") ?? (leadArv ? "$" + leadArv.toLocaleString() : null);
            const closingTxt = money(open.fields, "t_closing");
            const county = (() => {
              const e = edits.get(natKeyOf(open.address));
              const c = e && typeof e["county"] === "string" ? (e["county"] as string) : null;
              return c || (money(open.fields, "pp_county"));
            })();
            // v1 Project Lifetime (contract → today) and Days to Closing (today →
            // closing). Best-effort date parse; unparseable → not shown.
            const now = new Date();
            const contractStr = money(open.fields, "t_contract");
            const parseD = (s: string | null): Date | null => { if (!s) return null; const d = new Date(s); return Number.isNaN(d.getTime()) ? null : d; };
            const cD = parseD(contractStr);
            const clD = parseD(closingTxt);
            // Once CLOSED (stage ≥ 1): the project's life starts at the closing
            // date (Tom: "lifetime should start from the date it closed"), and a
            // countdown to a closing that already happened is meaningless — the
            // Days-to-Closing stat disappears.
            const isClosed = open.stageIdx >= 1;
            const lifeDays = isClosed && clD ? Math.max(0, daysBetween(clD, now)) : cD ? daysBetween(cD, now) : 0;
            const dtc = isClosed ? null : clD ? daysBetween(now, clD) : null;
            // Team avatars — the deal's assigned team, else the current user.
            const team = narrowDealTeam(open.fields["team"]);
            const avatars = team.length ? team.map((m) => m.n) : [me];
            const hueCls = ["hue-blue", "hue-violet", "hue-emerald"];
            const initials = (n: string) => n.split(/\s+/).map((s) => s[0] ?? "").join("").slice(0, 2).toUpperCase() || "?";
            // The stage gate, pulled up out of the wfbanner card so it can ride
            // as the stage row's second line on phones (see .pdmfacts below).
            const sName = stageName(open.stageIdx);
            const wfTotal = (WF_STAGE_TASKS[sName] ?? []).length;
            const wfDone = wfTotal
              ? wfTotal - Math.min(incompleteChecklist(open.address, sName, tasks).length, wfTotal)
              : 0;
            const nextStage = open.stageIdx + 1 < STAGE_NAMES.length ? stageName(open.stageIdx + 1) : null;
            const lifeTxt = `${lifeDays} day${lifeDays === 1 ? "" : "s"}${isClosed && clD ? " since closing" : " since contract"}`;
            return (
              <>
              {/* ---- PHONES: one label-value table ----------------------------
                  Same treatment the lead drawer got. The desktop header, its
                  5-card stat grid (which orphaned a fifth card on its own row),
                  the full-width Project Lifetime pill, the Team line, the three
                  stacked buttons and the Stage-workflow card were ~700px before
                  you reached a tab. All of it lands here in ~300px.

                  Nothing was dropped. Project Lifetime and the 0/5 gate are the
                  stage row's second line — they are all answers to "where is
                  this deal", so they belong in one block. The gate is tappable
                  and opens Checklists, which the banner never was. Team folds
                  into the Closing row; the Team tab still holds the full list.
                  Delete stops being a button two rows under a bright blue one
                  and becomes a quiet row at the bottom of the actions card.
                  Hidden above 720px — desktop is untouched. */}
              <div className="pdmfacts">
                <div className="pdmfhead">
                  <div className="pdmfthumb">🏠</div>
                  <div className="pdmfaddr">
                    {open.address}
                    {county ? <span className="pdmfcounty">{county}</span> : null}
                  </div>
                </div>
                <div className="pdmfstage">
                  <div className="pdmfrow tap">
                    <dt>Stage</dt>
                    <dd className="act">
                      <select
                        value={open.stageIdx}
                        disabled={!canEdit}
                        aria-label="Deal stage"
                        onChange={(e) => roMoveStage(open.natKey, Number(e.target.value))}
                      >
                        {STAGE_NAMES.map((s, i) => <option key={s} value={i}>{s}</option>)}
                      </select>
                      <span>{sName} ›</span>
                    </dd>
                  </div>
                  {wfTotal ? (
                    <button className="pdmfgate" onClick={() => setDtab("checklist")}>
                      <span className={wfDone === wfTotal ? "ok" : "todo"}>{wfDone}/{wfTotal} checklist done</span>
                      {nextStage ? ` · complete all to advance to ${nextStage}` : " · final stage"}
                    </button>
                  ) : null}
                  <div className="pdmflife" title={isClosed && clD ? "Days since the closing date" : "Days since the contract date"}>
                    {dtc !== null ? `Closing in ${dtc} day${dtc === 1 ? "" : "s"} · ` : ""}{lifeTxt}
                  </div>
                </div>
                <dl className="pdmftable">
                  <div className="pdmfrow num">
                    <dt>Purchase · ARV</dt>
                    <dd>{k.purchase ? "$" + k.purchase.toLocaleString() : "—"}<span className="pdmfmuted"> · </span>{arvTxt ?? "—"}</dd>
                  </div>
                  <div className="pdmfrow num">
                    <dt>Rehab{sowTotal !== null ? " (SOW)" : ""} · ROI</dt>
                    <dd>
                      {k.rehab ? moneyExact(k.rehab) : "—"}<span className="pdmfmuted"> · </span>
                      <span className={k.roi !== null ? "roi-" + k.tier : ""}>{k.roi !== null ? k.roi.toFixed(1) + "%" : "—"}</span>
                    </dd>
                  </div>
                  <div className="pdmfrow">
                    <dt>Closing · team</dt>
                    <dd className="pdmfteam">
                      <span>{closingTxt ?? "—"}</span>
                      {avatars.slice(0, 3).map((n, i) => (
                        <span key={i} className={"txnav " + (hueCls[i % hueCls.length] ?? "hue-blue")} title={n}>{initials(n)}</span>
                      ))}
                    </dd>
                  </div>
                </dl>
                <div className="pdmfacts-acts card">
                  {/* No "Full property view" on phones (Tom, 2026-07-29): the
                      Property tab is right there in the strip and shows the
                      same panels inline, so the row was a second door to a
                      room you are already standing in. */}
                  <button className="pdmfrow tap asbtn primary" onClick={() => setEsignOn(true)}>
                    <dt>Send for signature</dt><dd className="act">›</dd>
                  </button>
                  <button className="pdmfrow tap asbtn" onClick={() => setAssignKey(open.natKey)}>
                    <dt>Assign</dt><dd className="act">›</dd>
                  </button>
                  {canEdit ? (
                    <button
                      className="pdmfrow tap asbtn danger"
                      onClick={() =>
                        confirmDeal.ask(`Delete transaction "${open.address || "this deal"}"? This cannot be undone.`, () => {
                          roDeleteDeal(open.natKey);
                          setOpenKey(null);
                        })
                      }
                    >
                      <dt>Delete deal</dt><dd className="act">›</dd>
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="txnhead">
                <div className="txnhl">
                  <div className="txnthumb">🏠</div>
                  <div className="txnhinfo">
                    <div className="txnhtop">
                      <h1>{open.address}</h1>
                      <select
                        className={"txnstage " + (STAGE_CLASS[open.stageIdx] ?? "")}
                        value={open.stageIdx}
                        disabled={!canEdit}
                        onChange={(e) => roMoveStage(open.natKey, Number(e.target.value))}
                      >
                        {STAGE_NAMES.map((s, i) => <option key={s} value={i}>{s}</option>)}
                      </select>
                      <span className="txnlife" title={isClosed && clD ? "Days since the closing date" : "Days since the contract date"}>
                        Project Lifetime: {lifeDays} day{lifeDays === 1 ? "" : "s"}{isClosed && clD ? " since closing" : ""}
                      </span>
                    </div>
                    {county ? <div className="txncounty">{county}</div> : null}
                    <div className="txnstats">
                      <span>$ Purchase <b>{k.purchase ? "$" + k.purchase.toLocaleString() : "—"}</b></span>
                      <span>ARV <b className="arv">{arvTxt ?? "—"}</b></span>
                      <span title={sowTotal !== null ? "Total from this deal's Scope of Work" : "Estimate from the property (no SOW yet)"}>
                        Rehab <b>{k.rehab ? moneyExact(k.rehab) : "—"}</b>{sowTotal !== null ? <em className="dim"> SOW</em> : null}
                      </span>
                      <span>Proj. ROI <b className={k.roi !== null ? "roi-" + k.tier : ""}>{k.roi !== null ? k.roi.toFixed(1) + "%" : "—"}</b></span>
                      <span>Closing <b>{closingTxt ?? "—"}</b></span>
                      {dtc !== null ? <span>Days to Closing <b className={dtc < 7 ? "hot" : ""}>{dtc + " days"}</b></span> : null}
                    </div>
                    <div className="txnteam">
                      <span className="txnteaml">Team</span>
                      {avatars.slice(0, 4).map((n, i) => (
                        <span key={i} className={"txnav " + (hueCls[i % hueCls.length] ?? "hue-blue")} title={n}>{initials(n)}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="txnhr">
                  {/* "Full property view" removed on desktop too (Tom
                      2026-07-30) — the Property tab shows the same panels
                      inline, so the button was a second door here as well. */}
                  <button className="btn slim" onClick={() => setEsignOn(true)} title="Send a document for e-signature (BoldSign)">✍ Send for Signature</button>
                  <button className="btn slim" onClick={() => setAssignKey(open.natKey)} title="Assign / label">⋯ Assign</button>
                  {canEdit ? (
                    <button
                      className="btn slim del ghost"
                      title="Delete this deal"
                      onClick={() =>
                        /* v1 deleteTxnCard — typed-delete, exact wording */
                        confirmDeal.ask(`Delete transaction "${open.address || "this deal"}"? This cannot be undone.`, () => {
                          roDeleteDeal(open.natKey);
                          setOpenKey(null);
                        })
                      }
                    >
                      🗑 Delete
                    </button>
                  ) : null}
                </div>
              </div>
              </>
            );
          })()}

          {!canEdit ? (
            <div className="drawerro">👁 Read-only — deal editing unlocks once <code>MIGRATION_CRM2_PHASE5_DEALS.sql</code> runs. You can view everything; changes won't save yet.</div>
          ) : null}

          {/* Desktop only: on phones this same gate is the stage row's second
              line inside .pdmfacts, and repeating it there would be a whole
              card saying what the line above it already said. */}
          {(() => {
            if (isPhone) return null;
            const sName = stageName(open.stageIdx);
            const total = (WF_STAGE_TASKS[sName] ?? []).length;
            if (!total) return null;
            const inc = incompleteChecklist(open.address, sName, tasks).length;
            const done = total - Math.min(inc, total);
            const next = open.stageIdx + 1 < STAGE_NAMES.length ? stageName(open.stageIdx + 1) : null;
            return (
              <div className="wfbanner">
                <span>
                  <strong>Stage workflow — {sName}</strong>
                  <span className="sub" style={{ display: "block" }}>
                    {done}/{total} checklist tasks done{next ? ` · complete all to advance to ${next}` : " · final stage"}
                  </span>
                </span>
                <span className={"lbadge " + (done === total ? "bon" : "bdup")}>{done}/{total}</span>
              </div>
            );
          })()}

          {(() => {
            const dk = natKeyOf(open.address);
            const checklistCount = tasks.filter((t) => t.deal && natKeyOf(t.deal) === dk).length;
            const showingsCount = narrowDealShowings(open.fields["showings"]).length;
            const expensesCount = Array.isArray(orgSettings["TXNEXP"])
              ? (orgSettings["TXNEXP"] as unknown[]).filter((r) => r !== null && typeof r === "object" && natKeyOf(String((r as Record<string, unknown>)["deal"] ?? "")) === dk).length
              : 0;
            return (
              /* 19 tabs on a 390px screen. The wrapper is display:contents on
                 desktop, so this is a phone-only change: the strip fades at its
                 right edge (a half-cut tab is the only honest signal that there
                 is more), the chevron scrolls it for anyone who does not think
                 to swipe a tab bar, and the pin puts the three or four you
                 actually use at the front. Same components as the lead drawer. */
              <div className="pdtabswrap dtabswrap">
                <div
                  className={"dtabbar wrap" + (tabsAtEnd ? " atend" : "")}
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
                    setTabsAtEnd((p) => (p === end ? p : end));
                  }}
                >
                  {orderedDealTabs.map((t) => {
                    const cnt = t.count === "checklist" ? checklistCount
                      : t.count === "showings" ? showingsCount
                      : t.count === "expenses" ? expensesCount : 0;
                    return (
                      <button
                        key={t.id}
                        className={"dtab chip" + (dtab === t.id ? " on" : "") + (pinnedTabs.includes(t.id) ? " pinned" : "")}
                        onClick={() => setDtab(t.id)}
                      >
                        <span className="dtabic">{t.icon}</span>{t.label}
                        {cnt ? <span className="dtabct">{cnt}</span> : null}
                      </button>
                    );
                  })}
                </div>
                <button
                  className="pdtabmore"
                  aria-label="More tabs"
                  onClick={(e) => {
                    const strip = e.currentTarget.parentElement?.querySelector(".dtabbar");
                    strip?.scrollBy({ left: Math.round(strip.clientWidth * 0.7), behavior: "smooth" });
                  }}
                >›</button>
                {onSaveUiPrefs ? (
                  <button className="pdtabpin" aria-label="Choose pinned tabs" title="Pin the tabs you use most" onClick={() => setPinOpen(true)}>
                    <Icon name="pin" size={16} />
                  </button>
                ) : null}
              </div>
            );
          })()}
          {pinOpen ? (
            <div className="pdmscrim" onClick={() => setPinOpen(false)}>
              <div className="pdmsheet" onClick={(e) => e.stopPropagation()}>
                <div className="pdmhd">Pin the tabs you use most</div>
                <p className="pdpinnote">Pinned tabs move to the front. Everything else keeps its order, so nothing shifts around on you.</p>
                {DEAL_TABS.map((t) => (
                  <button key={t.id} className={"pdmitem pin" + (pinnedTabs.includes(t.id) ? " on" : "")} onClick={() => toggleDealPin(t.id)}>
                    <Icon name="pin" size={15} />
                    <span>{t.icon} {t.label}</span>
                    {pinnedTabs.includes(t.id) ? <span className="pdpinon">Pinned</span> : null}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

            <div className="txngrid">
              <div className="txnmain">

            {/* v1 Purchasing tab: Purchasing / Inspection & Timeline / Title /
                Billing Info / Insurance Info / HOA Info — same t_* field registry. */}
            {dtab === "purchasing" ? (
              <>
                <DealSections deal={open} onEditField={roEditField} only={["numbers", "timeline", "title", "dispo", "utilities", "insurance", "hoa"]} pastContacts={pastContacts} />
                {/* Tom 2026-09-10: the partnership MOU, filled from HIS template
                    with this property + the partner side, "under purchasing at the bottom" */}
                <div className="cabar cabar-bottom">
                  <button className="btn slim primary" onClick={() => setMouOn(true)} title="Fill your partnership MOU template for this property">
                    🤝 Partnership agreement (MOU)
                  </button>
                  <span className="sub">Your Memorandum of Understanding, filled for this property — partner names, LLCs, EINs, address and parcel.</span>
                </div>
              </>
            ) : null}

            {/* v1 Rehab tab: SOW + Contractors + Vendors, plus the Rehab Timeline
                and Property Access FAQ field groups (r_* keys). */}
            {dtab === "rehab" ? (
              <>
                <div className="subtabwrap">
                  <div className="subtablbl">↳ Inside Rehab</div>
                  <div className="subtabbar">
                    {([["details", "📄 Details"], ["sow", "🔧 SOW"], ["photos", "🖼 Progress Photos"]] as const).map(([id, label]) => (
                      <button key={id} className={"subtab" + (rsub === id ? " on" : "")} onClick={() => setRsub(id)}>{label}</button>
                    ))}
                  </div>
                </div>
                {rsub === "details" ? (
                  <>
                    <DealSections deal={open} onEditField={roEditField} only={["rehab", "access2"]} />
                    <ContractorsSection address={open.address} orgSettings={orgSettings} onSave={roSaveContractors} {...(roPostExpense ? { onPostExpense: roPostExpense } : {})} canWrite={canEdit} probing={canEditProbing} />
                    <RelatedVendors deal={open} contacts={contacts} onSaveBlob={roSaveBlob} />
                  </>
                ) : null}
                {rsub === "sow" ? (
                  <SowSection dealKey={open.natKey} token={token} fetchSow={fetchSow} onOps={roSowOps} onForceSave={onSowForceSave} materials={materials} onNeedMaterials={roNeedMaterials} {...(canEdit && onPutMaterial ? { onPutMaterial } : {})} materialCats={materialCats} {...(onImportUrl ? { onImportUrl } : {})} sqft={dealSqft(open, edits)} {...(canEdit ? { onSqft: (k: "living" | "gross", v: number | null) => onEditField(open.natKey, k === "living" ? "pp_sqft" : "pp_grossSqft", v === null ? null : String(v)) } : {})} sharedTemplates={sharedSowTemplates} scopeTplsRaw={Array.isArray(orgSettings["SCOPE_TPLS"]) ? (orgSettings["SCOPE_TPLS"] as unknown[]) : []} {...(canEdit && onSaveScopeTpls ? { onSaveScopeTpls } : {})} onFlagTask={(catName) => roAddTask("Follow-up: " + catName, { deal: open.address })} />
                ) : null}
                {rsub === "photos" ? (
                  <ProgressPhotos onOpenDrive={() => setDtab("docs")} />
                ) : null}
              </>
            ) : null}

            {/* v1 Selling tab: Listing Information / Agent / Buyer Details fields
                (sl_* keys) + the repair/disclosure/remarks builder, split into
                v1's Inside-Selling sub-tabs. */}
            {dtab === "selling" ? (
              <>
                <div className="subtabwrap">
                  <div className="subtablbl">↳ Inside Selling</div>
                  <div className="subtabbar">
                    {([["details", "📄 Details"], ["disclosure", "📝 Seller Disclosure"], ["photos", "🖼 Listing Photos"], ["repair", "🔧 Repair Addendum"], ["remarks", "✨ Public Remarks"]] as const).map(([id, label]) => (
                      <button key={id} className={"subtab" + (ssub === id ? " on" : "")} onClick={() => setSsub(id)}>{label}</button>
                    ))}
                  </div>
                </div>
                {ssub === "details" ? (
                  <>
                    <DealSections deal={open} onEditField={roEditField} only={["sellist", "selagent", "selbuyer"]} />
                    {(() => {
                      const c = dealSellingComputed(open.fields);
                      return (
                        <div className="calccards">
                          <div className="calccard">
                            <div className="fld-l">Buyer Inspection Period End Date</div>
                            <div className="calcval">{c.inspEnd || "—"}</div>
                            <div className="calcnote">Buyer Signature Date + Inspection Period</div>
                          </div>
                          <div className="calccard">
                            <div className="fld-l">Days to Pending</div>
                            <div className="calcval">{c.daysToPending || "—"}</div>
                            <div className="calcnote">Listing Date to Buyer Signature Date</div>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                ) : null}
                {ssub === "disclosure" ? (
                  <SellingSection deal={open} canEdit={canEdit} onSaveBlob={roSaveBlob} ai={sellingAi} sub="disclosure" />
                ) : null}
                {ssub === "photos" ? (
                  <ProgressPhotos onOpenDrive={() => setDtab("docs")} kind="listing" />
                ) : null}
                {ssub === "repair" ? (
                  <SellingSection deal={open} canEdit={canEdit} onSaveBlob={roSaveBlob} ai={sellingAi} sub="repair" />
                ) : null}
                {ssub === "remarks" ? (
                  <SellingSection deal={open} canEdit={canEdit} onSaveBlob={roSaveBlob} ai={sellingAi} sub="remarks" fetchSow={fetchSow} />
                ) : null}
              </>
            ) : null}

            {/* v1 txnExpensesPanel: Contractors section first, then the deal's
                expense ledger (header · search · 7-col table · totals). */}
            {dtab === "expenses" ? (
              <>
                {/* Tom 2026-09-04: generate the contractor agreement from HIS
                    Word template with this deal's address/dates/price filled */}
                <div className="cabar">
                  <button className="btn slim primary" onClick={() => setAgreementOn(true)} title="Fill your contractor agreement template for this project">
                    📄 Contractor agreement
                  </button>
                  <span className="sub">Your Independent Contractor Agreement, filled for this project — pick the contractor, price, dates and payment split.</span>
                </div>
                <ContractorsSection address={open.address} orgSettings={orgSettings} onSave={roSaveContractors} {...(roPostExpense ? { onPostExpense: roPostExpense } : {})} canWrite={canEdit} probing={canEditProbing} />
                <TxnExpensesSection address={open.address} orgSettings={orgSettings} contacts={contacts} onSave={roSaveTxnExpenses} canWrite={canEdit} probing={canEditProbing} />
              </>
            ) : null}

            {/* v1 Property tab: Property Info + Financials (pp_* keys). */}
            {dtab === "property" ? <DealSections deal={open} onEditField={roEditField} only={["propinfo", "propfin"]} /> : null}

            {/* Property-record tabs, INLINE (Tom 2026-07-19): the same panels
                the property drawer renders, against the linked lead — or a
                synthetic subject when the deal was added manually. */}
            {dtab === "comps" && propSubject ? (
              <CompsPanel
                lead={propSubject}
                fields={propFields}
                inline
                onClose={() => {}}
                onSearch={compsSearch}
                onPhotos={compsPhotos}
                onPersist={onCompsPersist}
                onApplyArv={onCompsArv}
              />
            ) : null}

            {dtab === "listing" ? (
              propLead ? (
                <>
                  <OverviewSection lead={propLead} />
                  <ContactDocsCard docs={propLead.photoLinks ?? []} />
                  <PriceHistorySection lead={propLead} />
                  {!propLead.desc && !(propLead.photoLinks ?? []).length && propLead.priceDrops.length + propLead.priceHistory.length === 0 ? (
                    <div className="cmempty">No listing details captured for this property yet — the overview and price history fill in when the listing is scraped.</div>
                  ) : null}
                </>
              ) : (
                <div className="cmempty">No listing record linked — this deal was added manually, so there's no scraped listing overview or price history for it.</div>
              )
            ) : null}

            {dtab === "tax" && propSubject ? (
              <>
                <TaxSections lead={propSubject} fields={propFields} onField={onLeadField} />
                <TaxHistorySection lead={propSubject} alwaysShow />
                <div className="sub taxdisclaimer">Values come from the county appraiser + Zillow and may lag the latest tax roll. Verify with the county before making an offer.</div>
              </>
            ) : null}

            {dtab === "history" ? (
              propLead && propLead.priceDrops.length + propLead.priceHistory.length > 0
                ? <PriceHistorySection lead={propLead} startOpen />
                : <div className="cmempty">No price history captured yet — it's pulled from the listing when the property is scraped.</div>
            ) : null}

            {dtab === "showings" ? (
              <DealShowings
                deal={open}
                onSaveBlob={roSaveBlob}
                stFeed={typeof orgSettings["SHOWINGTIME_FEED"] === "string" ? (orgSettings["SHOWINGTIME_FEED"] as string) : ""}
                {...(onShowingFeed ? { onFeed: onShowingFeed } : {})}
              />
            ) : null}

            {dtab === "docs" ? (
              <DriveSection
                address={open.address}
                dispo={false}
                loaders={driveLoaders}
                labels={open.driveLabels ?? {}}
                {...(canEdit && onLabelDriveFile ? { onLabel: (fileId: string, label: string | null) => onLabelDriveFile(open.natKey, fileId, label) } : {})}
              />
            ) : null}

            {dtab === "activity" ? <DealActivity deal={open} tasks={tasks} /> : null}

            {dtab === "team" ? <DealTeam deal={open} onSaveBlob={roSaveBlob} me={me} /> : null}

            {dtab === "permits" && propSubject ? (
              <PermitsSection lead={propSubject} fields={propFields} onAiSearch={onAiPermitSearch} startOpen />
            ) : null}

            {dtab === "checklist" ? (
              <DealChecklist
                address={open.address}
                tasks={tasks}
                members={members}
                onToggleTask={roToggleTask}
                onAddTask={roAddTask}
              />
            ) : null}

            {dtab === "ai" && propSubject ? (
              <ArvSection lead={propSubject} fields={propFields} onRun={onRunArv} onSaveResult={onSaveArvResult} onField={onLeadField} startOpen />
            ) : null}

            {/* v1 Insurance tab: "Insurance Policies" upload panel. The carrier +
                expiry fields are shown inline here (also on Purchasing); document
                uploads sync through the Drive tab. */}
            {dtab === "insurance" ? (
              <>
                <DealSections deal={open} onEditField={roEditField} only={["insurance"]} />
                <div className="dbridge">
                  <span className="dbicon">🛡️</span>
                  <strong>Insurance Policies</strong>
                  <span className="sub">Upload a policy PDF or image to keep coverage details on file — policy documents sync through the <b>Drive</b> tab (Insurance category).</span>
                  <button className="btn slim" onClick={() => setDtab("docs")}>Open Drive → upload policy</button>
                </div>
              </>
            ) : null}

            {dtab === "renovai" ? (
              <div className="dbridge">
                <span className="dbicon">🪄</span>
                <strong>RenovAI</strong>
                <span className="sub">AI renovation visualizations require an image-generation endpoint (not yet wired in v2 — same as v1's preview).</span>
              </div>
            ) : null}

            {/* v1 leadMailPanel — the seller/agent email thread, inline. The
                seller email lives on the linked lead's edits, so merge those
                under the deal's own fields (deal fields win on conflict). */}
            {dtab === "emails" ? (
              <EmailsSection
                address={open.address}
                fields={{ ...(edits.get(natKeyOf(open.address)) ?? {}), ...open.fields }}
                loaders={mailLoaders}
                startOpen
              />
            ) : null}

                <p className="sub" style={{ marginTop: 8 }}>
                  Changes sync to v1's Transactions board automatically.
                </p>
              </div>

              {/* v1 txnDetailView right sidebar: Property Info / Contacts / Tasks / Documents.
                  On desktop this is a 330px column PINNED beside the tab content, so
                  "always visible" is the point. Under 900px the grid collapses to one
                  column and it drops BELOW the tab body — which reads as the same five
                  cards repeating under every one of the 17 tabs.

                  It is not gated by tab on purpose: Contacts and Tasks are just as
                  useful in Rehab or Selling as in Purchasing, so burying them in one
                  tab would mean hunting for the title company's number. Instead it
                  collapses on phones — one row you open when you want it, nothing
                  moved and nothing hidden. */}
              <details className="txnsidewrap">
                <summary className="txnsidesum">Property, contacts &amp; tasks</summary>
              <aside className="txnside">
                <TxnPropertyInfo deal={open} edits={edits} />
                <TxnContactsCard contacts={dealContacts(edits.get(natKeyOf(open.address)), open.fields)} />
                <TxnTasksCard address={open.address} tasks={tasks} onToggleTask={roToggleTask} onOpenTab={() => setDtab("checklist")} />
                <TxnDocsCard onOpen={() => setDtab("docs")} />
                <DealNotes key={open.natKey + ":notes"} deal={open} onEditField={roEditField} />
              </aside>
              </details>
            </div>
        </div>
      ) : null}
      {assignModal}
      {open && esignOn ? <EsignModal token={token} address={open.address} onClose={() => setEsignOn(false)} /> : null}
      {open && mouOn ? (
        <PartnershipMouModal
          token={token}
          isAdmin={isAdmin}
          seed={{
            propertyAddress: open.address,
            legalDescription: typeof open.fields["pp_parcel"] === "string" ? (open.fields["pp_parcel"] as string) : null,
            partnerHint: open.partner ?? null,
          }}
          onClose={() => setMouOn(false)}
        />
      ) : null}
      {open && agreementOn ? (
        <ContractorAgreementModal
          token={token}
          isAdmin={isAdmin}
          contacts={contacts}
          seed={{
            projectAddress: open.address,
            // the SOW total is the number Tom actually manages; else the card's rehab / budget field
            price: sowTotal ?? (open.rehab || null) ?? (typeof open.fields["r_budget"] === "string" ? Number(String(open.fields["r_budget"]).replace(/[^0-9.]/g, "")) || null : null),
            startDate: typeof open.fields["r_start"] === "string" ? (open.fields["r_start"] as string) : null,
            completionDate: typeof open.fields["r_pfinish"] === "string" ? (open.fields["r_pfinish"] as string) : null,
          }}
          onClose={() => setAgreementOn(false)}
        />
      ) : null}
      {confirmDeal.modal}
    </div>
  );
}

/** Living + gross square footage for a deal, from its pp_* fields first, then the
 * linked lead's edits — same source/keys the rehab calculator and property panel
 * use (living: pp_sqft/sqft/countySqft; gross: totalSqft/countyGrossArea). */
function dealSqft(deal: Deal, edits: Map<string, Record<string, unknown>>): { living: number | null; gross: number | null } {
  const f = deal.fields;
  const le = edits.get(natKeyOf(deal.address));
  const num = (k: string): number | null => {
    const v = f[k] ?? le?.[k];
    const nn = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[^0-9.]/g, ""));
    return Number.isFinite(nn) && nn > 0 ? nn : null;
  };
  return {
    living: num("pp_sqft") ?? num("sqft") ?? num("countySqft"),
    gross: num("pp_grossSqft") ?? num("pp_totalSqft") ?? num("totalSqft") ?? num("countyGrossArea"),
  };
}

/** v1 txnDetailView right sidebar — PROPERTY INFO (address/type/sqft/beds/year),
 * pulled from the deal's pp_* fields first, then the linked lead's edits. */
function TxnPropertyInfo({ deal, edits }: { deal: Deal; edits: Map<string, Record<string, unknown>> }) {
  const f = deal.fields;
  const le = edits.get(natKeyOf(deal.address));
  const val = (k: string): string | null => {
    const v = f[k];
    if (typeof v === "string" && v.trim()) return v;
    const lv = le?.[k];
    return typeof lv === "string" && lv.trim() ? lv : null;
  };
  const beds = val("pp_beds"), baths = val("pp_baths");
  const rows: [string, string][] = [
    ["Address", deal.address],
    ["Type", val("pp_type") ?? "—"],
    ["Sqft", val("pp_sqft") ?? "—"],
    ["Beds / Baths", beds || baths ? `${beds ?? "—"} / ${baths ?? "—"}` : "—"],
    ["Year Built", val("pp_yr") ?? "—"],
    ["County", val("pp_county") ?? "—"],
  ];
  return (
    <div className="txncard">
      <h3 className="txch3">PROPERTY INFO</h3>
      <div className="txkv">
        {rows.map(([l, v]) => (
          <div key={l} className="txkvrow"><span>{l}</span><b>{v}</b></div>
        ))}
      </div>
    </div>
  );
}

/** v1 txnDetailView right sidebar — CONTACTS (Seller / Dispo / Title), always
 * expanded (unlike the collapsible drawer card), tap-to-call / email. */
function TxnContactsCard({ contacts }: { contacts: import("./api").DealContact[] }) {
  return (
    <div className="txncard">
      <h3 className="txch3">CONTACTS</h3>
      {contacts.length === 0 ? (
        <div className="txcempty">No contacts yet — add the seller on the property's Details tab.</div>
      ) : (
        contacts.map((c, i) => (
          <div key={i} className="txncontact">
            <span className="txcrole">{c.role}</span>
            <div className="txcmain">
              <strong>{c.name || c.company || "—"}</strong>
              {c.name && c.company ? <span className="sub">{c.company}</span> : null}
            </div>
            <span className="txcacts">
              {c.phone ? <a href={`tel:${c.phone}`} title={c.phone}>📞</a> : null}
              {c.email ? <a href={`mailto:${c.email}`} title={c.email}>✉️</a> : null}
            </span>
          </div>
        ))
      )}
    </div>
  );
}

/** v1 txnDetailView right sidebar — TASKS (this deal's tasks, checkable),
 * with a jump to the full Checklists tab. */
function TxnTasksCard({ address, tasks, onToggleTask, onOpenTab }: {
  address: string;
  tasks: TaskRec[];
  onToggleTask: (key: string, done: boolean) => void;
  onOpenTab: () => void;
}) {
  const k = natKeyOf(address);
  // OPEN tasks first, checked ones sink to the bottom (Tom 2026-07-24); within
  // each group, auto checklist items before manual ones, original order kept.
  const mine = tasks
    .filter((t) => t.deal && natKeyOf(t.deal) === k)
    .sort((a, b) =>
      Number(isTaskDone(a.status)) - Number(isTaskDone(b.status)) ||
      Number(b.auto ?? false) - Number(a.auto ?? false));
  const done = mine.filter((t) => isTaskDone(t.status)).length;
  return (
    <div className="txncard">
      <div className="txchd"><h3 className="txch3">TASKS</h3><button className="txclink" onClick={onOpenTab}>Open →</button></div>
      <div className="txcsub">{done}/{mine.length} done</div>
      <div className="txntasks">
        {mine.length === 0 ? <div className="txcempty">No tasks for this deal yet.</div> : null}
        {mine.slice(0, 8).map((t) => (
          <label key={t.natKey} className="txntask">
            <input type="checkbox" checked={isTaskDone(t.status)} onChange={(e) => onToggleTask(t.natKey, e.target.checked)} />
            <span className={isTaskDone(t.status) ? "done" : ""}>
              {t.auto ? <span className="lbadge boff" title={"Stage checklist" + (t.stage ? ": " + t.stage : "")}>auto</span> : null} {t.title}
            </span>
          </label>
        ))}
        {mine.length > 8 ? <button className="txclink" onClick={onOpenTab}>+{mine.length - 8} more in Checklists →</button> : null}
      </div>
    </div>
  );
}

/** v1 txnDetailView right sidebar — DOCUMENTS (jumps to the Drive tab). */
function TxnDocsCard({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="txncard">
      <div className="txchd"><h3 className="txch3">DOCUMENTS</h3><button className="txclink" onClick={onOpen}>Open Drive →</button></div>
      <div className="txcsub">Contracts, disclosures &amp; reports — synced to Google Drive.</div>
    </div>
  );
}

/** v1 Progress-Photos (Inside-Rehab sub-tab #3) and Listing-Photos (Inside-
 * Selling sub-tab #3). Photos live in Google Drive in v1 — this is the category
 * chooser + drop zone; the actual upload/queue happens through the Drive tab, so
 * both variants link there. `kind="listing"` renders v1's simpler Drive-first
 * empty state; the default renders the category-pill progress board. */
const PHOTO_CATS = ["General Progress", "Kitchen", "Bathroom", "Roof", "Demo", "Exterior", "Interior", "HVAC", "Plumbing", "Other", "Listing"] as const;
function ProgressPhotos({ onOpenDrive, kind = "rehab" }: { onOpenDrive: () => void; kind?: "rehab" | "listing" }) {
  const [cat, setCat] = useState<string>("General Progress");
  if (kind === "listing") {
    return (
      <div className="detailsec open">
        <div className="sechead static">
          <span>🖼 Listing Photos</span>
          <button className="btn slim linkish" onClick={onOpenDrive}>Open Drive → Listing Pictures</button>
        </div>
        <div className="photozone empty">
          <div className="pzicon">🖼</div>
          <div className="pztitle">No listing photos yet</div>
          <div className="pzsub">Upload them in the Drive tab — they sync to Google Drive</div>
        </div>
      </div>
    );
  }
  return (
    <div className="detailsec open">
      <div className="sechead static"><span>🖼 Progress Photos</span></div>
      <div className="photobody">
        <div className="fld-l">Category</div>
        <div className="photocats">
          {PHOTO_CATS.map((c) => (
            <button key={c} className={"photopill" + (cat === c ? " on" : "")} onClick={() => setCat(c)}>{c}</button>
          ))}
        </div>
        <button className="btn slim primary photoadd" onClick={onOpenDrive}>📷 Add Photos</button>
        <div className="photozone" onClick={onOpenDrive} role="button" tabIndex={0}>
          <div className="pzicon">📷</div>
          <div className="pztitle">Drop photos &amp; videos here</div>
          <div className="pzsub">or click to browse</div>
          <div className="pzhint">Supports: JPG, PNG, HEIC, MP4, MOV · Queued automatically</div>
        </div>
      </div>
    </div>
  );
}

/** v1 renderAssignModal — Internal Assignment (Partner/Investor) + deal label.
 * "Internal label for admin tracking. Does NOT grant portal access." The card's
 * label pill + partner dot are driven from what's saved here. Deletion of a deal
 * stays in v1 (v2 has no delete op), so no Delete button — otherwise field-for
 * field with v1's modal. */
function AssignModal({
  deal,
  canEdit,
  onClose,
  onSave,
  onDelete,
}: {
  deal: Deal;
  canEdit: boolean;
  onClose: () => void;
  onSave: (label: string | null, partner: { type: string; name: string; color: string; note: string } | null) => void;
  onDelete: () => void;
}) {
  const [label, setLabel] = useState<string>(deal.label ?? "Flip");
  const [type, setType] = useState<string>(deal.partnerType ?? (deal.partner ? "Partner" : "Unassigned"));
  const [color, setColor] = useState<string>(deal.partnerColor ?? "blue");
  const [name, setName] = useState<string>(deal.partner ?? "");
  const [note, setNote] = useState<string>(deal.partnerNote ?? "");
  // v1 typed-delete — the type-"delete" modal guards the deal delete here too.
  const confirmDel = useConfirmDelete();

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const save = () => {
    if (!canEdit) { onClose(); return; }
    const partner = type === "Unassigned"
      ? null
      : { type, name: name.trim() || type, color: color || "blue", note: note.trim() };
    onSave(label, partner);
  };

  return (
    <>
      <div className="scrim asg" onClick={onClose} />
      <div className="asgmodal" role="dialog" aria-label="Internal Assignment">
        <header className="asghd">
          <h2><span className="asgicon">👥</span> Internal Assignment</h2>
          <button className="close" onClick={onClose} title="Close (Esc)">×</button>
        </header>
        <div className="asgbody">
          <p className="asgnote">Internal label for admin tracking. Does NOT grant portal access.</p>

          <div className="asgsec">
            <div className="asgh">Deal Label</div>
            <div className="asglabels">
              {DEAL_LABELS.map((l) => (
                <button
                  key={l}
                  className={"kbpill " + (LABEL_CLASS[l] ?? "flip") + (label === l ? " sel" : "")}
                  onClick={() => setLabel(l)}
                >{l}</button>
              ))}
            </div>
          </div>

          <div className="asgsec">
            <div className="asgh">Assignment Type</div>
            <div className="asgtypes">
              {ASSIGN_TYPES.map(({ t, ic }) => (
                <button
                  key={t}
                  className={"asgtype" + (type === t ? " on" : "")}
                  onClick={() => setType(t)}
                >
                  <span className="asgtic">{ic}</span>
                  <span className="asgtl">{t}</span>
                </button>
              ))}
            </div>
          </div>

          {type !== "Unassigned" ? (
            <>
              <div className="asgsec">
                <div className="asgh">{type} Name</div>
                <input className="asginput" value={name} placeholder="e.g. Test Test" onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="asgsec">
                <div className="asgh">🎨 Assignment Color</div>
                <div className="asgcolors">
                  {ASSIGN_COLORS.map((c) => (
                    <button
                      key={c}
                      className={"asgsw" + (color === c ? " on" : "")}
                      title={c}
                      style={{ background: ASSIGN_HUES[c] }}
                      onClick={() => setColor(c)}
                    />
                  ))}
                </div>
              </div>
              <div className="asgsec">
                <div className="asgh">Internal Note (admin only)</div>
                <textarea className="asgta" rows={3} value={note} placeholder="Why assigned / relationship / next step…" onChange={(e) => setNote(e.target.value)} />
              </div>
            </>
          ) : null}
        </div>
        <footer className="asgfoot">
          {canEdit ? (
            <button
              className="btn slim del ghost"
              title="Delete this deal"
              onClick={() => confirmDel.ask(`Delete transaction "${deal.address || "this deal"}"? This cannot be undone.`, onDelete)}
            >
              🗑 Delete Deal
            </button>
          ) : null}
          <div className="asgspacer" />
          <button className="btn slim" onClick={onClose}>Cancel</button>
          <button className="btn slim primary" onClick={save} disabled={!canEdit} title={canEdit ? "" : "Editing unlocks when the deals migration runs"}>Save Assignment</button>
        </footer>
      </div>
      {confirmDel.modal}
    </>
  );
}

/** All 24 v1 deal-card fields (DEAL_FIELDS registry), collapsible groups —
 * same pattern as the lead drawer's DetailSections. Deal Numbers opens by
 * default; every commit writes the exact t_* key v1 reads. */
function DealSections({
  deal,
  onEditField,
  only,
  pastContacts,
}: {
  deal: Deal;
  onEditField: (natKey: string, field: string, value: string | null) => void;
  /** Restrict to these group ids (v1 split the same field registry across the
   * Purchasing / Property / Rehab / Selling tabs). Omit = all groups. */
  only?: string[];
  /** past title/dispo contacts for the one-pick fill (2026-08-31) */
  pastContacts?: PastContacts | undefined;
}) {
  // Render in the caller's `only` order (v1's per-tab section order), not the
  // DEAL_GROUPS declaration order — v1 shows e.g. Title before HOA on Purchasing.
  const groups = only
    ? only.flatMap((id) => { const g = DEAL_GROUPS.find((x) => x.id === id); return g ? [g] : []; })
    : DEAL_GROUPS;
  const isPhone = usePhone();
  /* Same fold as the lead drawer's FieldGrid (Tom 2026-07-30: "do the 'fill a
     field' to all the tabs"): filled fields are slim tap-to-edit rows, empty
     ones hide behind one "+ Fill a field" row per section. Purchasing alone
     is six sections of inputs — this is where the wall of "—" boxes lived. */
  const [showEmpty, setShowEmpty] = useState<Set<string>>(() => new Set());
  const [editing, setEditing] = useState<Set<string>>(() => new Set());
  useEffect(() => { setShowEmpty(new Set()); setEditing(new Set()); }, [deal.natKey]);
  const val = (key: string): string | null => {
    const v = deal.fields[key];
    return typeof v === "string" && v.trim() ? v : null;
  };
  // Uncontrolled inputs seeded from `initial` — key on the VALUE too so an
  // external fill (past-contact picker, convert carry-over) remounts them
  // with the new text instead of showing stale emptiness (Tom 2026-08-31).
  const fkey = (f: (typeof DEAL_FIELDS)[number]) => deal.natKey + f.key + ":" + (val(f.key) ?? "");
  const inputFor = (f: (typeof DEAL_FIELDS)[number]) =>
    f.kind === "money" ? (
      <MoneyText
        key={fkey(f)}
        initial={val(f.key)}
        placeholder="—"
        onCommit={(v) => onEditField(deal.natKey, f.key, v)}
      />
    ) : f.kind === "date" ? (
      <input
        key={fkey(f)}
        className="moneyinput"
        type="date"
        defaultValue={toDateInput(val(f.key))}
        onChange={(e) => onEditField(deal.natKey, f.key, e.target.value ? fmtDateEdit(e.target.value) : null)}
      />
    ) : f.kind === "yesno" ? (
      <select
        key={deal.natKey + f.key}
        className="moneyinput"
        value={val(f.key) ?? ""}
        onChange={(e) => onEditField(deal.natKey, f.key, e.target.value || null)}
      >
        <option value="">—</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
        {val(f.key) && !["Yes", "No"].includes(val(f.key) as string) ? (
          <option value={val(f.key) as string}>{val(f.key)}</option>
        ) : null}
      </select>
    ) : (
      <DealText
        key={fkey(f)}
        initial={val(f.key)}
        onCommit={(v) => onEditField(deal.natKey, f.key, v)}
      />
    );
  return (
    <>
      {groups.map((g) => {
        const items = DEAL_FIELDS.filter((f) => f.group === g.id);
        const shown = (f: (typeof DEAL_FIELDS)[number]) => val(f.key) !== null || editing.has(f.key);
        const filledItems = items.filter(shown);
        const emptyItems = items.filter((f) => !shown(f));
        const filled = items.filter((f) => val(f.key) !== null).length;
        const pickKind: "title" | "dispo" | null = g.id === "title" ? "title" : g.id === "dispo" ? "dispo" : null;
        return (
          <div key={g.id} className="detailsec open">
            <div className="sechead static">
              <span>{g.label}</span>
              {filled ? <span className="sub">{filled} filled</span> : null}
            </div>
              {/* "pick from past" fill for Title + Dispo (Tom 2026-08-31) */}
              {pickKind && pastContacts && pastContacts[pickKind].length ? (
                <PastContactPicker
                  options={pastContacts[pickKind]}
                  kind={pickKind}
                  onPick={(c) => {
                    const keys = PAST_KEYS.deal[pickKind] as Record<string, string>;
                    const vals: Record<string, string> = { company: c.company, name: c.name, phone: c.phone, email: c.email };
                    for (const [slot, key] of Object.entries(keys)) {
                      const v = vals[slot] ?? "";
                      if (v) onEditField(deal.natKey, key, v);
                    }
                  }}
                />
              ) : null}
              <div className="secbody">
                {!isPhone ? (
                  items.map((f) => (
                    <div key={f.key} className="arvfield">
                      <label className="lbl">{f.label}</label>
                      {inputFor(f)}
                    </div>
                  ))
                ) : (
                  <>
                    {filledItems.map((f) =>
                      editing.has(f.key) ? (
                        <div key={f.key} className="arvfield" ref={focusOnMount}>
                          <label className="lbl">{f.label}</label>
                          {inputFor(f)}
                        </div>
                      ) : (
                        <button
                          key={f.key}
                          type="button"
                          className="kvread"
                          title="Tap to edit"
                          onClick={() => setEditing((s) => new Set(s).add(f.key))}
                        >
                          <span className="kvread-l">{f.label}</span>
                          <span className="kvread-v">{val(f.key)}</span>
                        </button>
                      ),
                    )}
                    {emptyItems.length && !showEmpty.has(g.id) ? (
                      <button
                        type="button"
                        className="kvmore"
                        onClick={() => setShowEmpty((s) => new Set(s).add(g.id))}
                      >
                        + Fill a field ({emptyItems.length} empty)
                      </button>
                    ) : null}
                    {showEmpty.has(g.id)
                      ? emptyItems.map((f) => (
                          <div key={f.key} className="arvfield">
                            <label className="lbl">{f.label}</label>
                            {inputFor(f)}
                          </div>
                        ))
                      : null}
                  </>
                )}
              </div>
          </div>
        );
      })}
    </>
  );
}

/** Deal Contacts (v1 transactions.js CONTACTS sidebar): seller / dispo / title,
 * derived from the linked lead's edits. Read-only cards with tap-to-call /
 * email; empty roles are omitted upstream by dealContacts(). */
function DealContacts({ contacts }: { contacts: import("./api").DealContact[] }) {
  if (contacts.length === 0) return null;
  return (
    <div className="detailsec open">
      <div className="sechead static">
        <span>Contacts</span>
        <span className="sub">{contacts.length}</span>
      </div>
        <div className="secbody">
          {contacts.map((c, i) => (
            <div key={i} className="dcontact">
              <div className="dcmain">
                <span className="dcrole">{c.role}</span>
                <strong>{c.name || c.company || "—"}</strong>
                {c.name && c.company ? <span className="sub">{c.company}</span> : null}
              </div>
              <div className="dcacts">
                {c.phone ? <a href={`tel:${c.phone}`} title={c.phone}>📞</a> : null}
                {c.email ? <a href={`mailto:${c.email}`} title={c.email}>✉️</a> : null}
              </div>
            </div>
          ))}
        </div>
    </div>
  );
}

/** Deal Activity (v2 derived, read-only): stage + checklist progress, then the
 * deal's filled date fields in chronological order. v1's Activity tab was a
 * never-written stub — this surfaces the real timeline instead. */
function DealActivity({ deal, tasks }: { deal: Deal; tasks: TaskRec[] }) {
  const k = natKeyOf(deal.address);
  const mine = tasks.filter((t) => t.deal && natKeyOf(t.deal) === k);
  const done = mine.filter((t) => isTaskDone(t.status)).length;
  const timeline = useMemo(() => dealTimeline(deal.fields), [deal.fields]);

  return (
    <div className="detailsec open">
      <div className="sechead static">
        <span>Activity & timeline</span>
      </div>
        <div className="secbody dactivity">
          <div className="dactline">
            <span className="dacttag">Stage</span>
            <span>{stageName(deal.stageIdx)}</span>
          </div>
          {mine.length ? (
            <div className="dactline">
              <span className="dacttag">Checklist</span>
              <span>{done}/{mine.length} tasks done</span>
            </div>
          ) : null}
          {deal.updatedAt ? (
            <div className="dactline">
              <span className="dacttag">Updated</span>
              <span>{new Date(deal.updatedAt).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}</span>
            </div>
          ) : null}
          {timeline.length === 0 ? (
            <span className="sub">No dates set yet — add closing / inspection / rehab dates above.</span>
          ) : (
            timeline.map((e, i) => (
              <div key={i} className="dactline">
                <span className="dacttag date">{e.label}</span>
                <span>{e.date}</span>
              </div>
            ))
          )}
        </div>
    </div>
  );
}

/** Per-deal Showings (v1 flip Showings tab). Stored on deal edits.showings. */
function DealShowings({ deal, onSaveBlob, stFeed = "", onFeed }: {
  deal: Deal;
  onSaveBlob: (natKey: string, key: string, value: unknown) => void;
  /** org SHOWINGTIME_FEED url (connected from v1's Showings tab) */
  stFeed?: string;
  onFeed?: (feedUrl: string, address: string) => Promise<StFeedResult>;
}) {
  const list = useMemo(() => narrowDealShowings(deal.fields["showings"]), [deal.fields]);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState<Omit<DealShowing, "id">>({ buyer: "", ph: "", dt: "", status: "Scheduled", access: "", feedback: "" });
  // ShowingTime Activity (v1 stFetch — go-live item 4): auto-sync on open,
  // manual Sync button, honest empty/error states.
  const [st, setSt] = useState<{ state: "idle" | "loading" | "ok" | "err"; events: StEvent[]; note: string }>({ state: "idle", events: [], note: "" });
  const syncSt = useCallback(async () => {
    if (!stFeed || !onFeed) return;
    setSt((p) => ({ ...p, state: "loading", note: "" }));
    const r = await onFeed(stFeed, deal.address);
    if (r.ok) setSt({ state: "ok", events: r.events, note: "" });
    else setSt({ state: "err", events: [], note: r.detail });
  }, [stFeed, onFeed, deal.address]);
  useEffect(() => { void syncSt(); }, [syncSt]);
  const save = (all: DealShowing[]) => onSaveBlob(deal.natKey, "showings", all);
  const add = () => {
    if (!f.buyer.trim() && !f.dt.trim()) return;
    save([{ ...f, id: dealShowingId() }, ...list]);
    setF({ buyer: "", ph: "", dt: "", status: "Scheduled", access: "", feedback: "" });
    setAdding(false);
  };
  const remove = (id: string) => save(list.filter((s) => s.id !== id));
  return (
    <div className="detailsec">
      {/* v1 ShowingTime Activity — live feed for THIS address (go-live item 4) */}
      <div className="stpanel">
        <div className="sthead">
          <span className="stttl">👁 ShowingTime Activity</span>
          {stFeed && onFeed ? (
            <button className="btn slim" disabled={st.state === "loading"} onClick={() => void syncSt()}>
              {st.state === "loading" ? "Syncing…" : "⟳ Sync"}
            </button>
          ) : null}
        </div>
        {!stFeed ? (
          <div className="sub stnote">No ShowingTime feed connected yet — connect it once from v1's Showings tab (Profile → Calendar Sync link) and it shows here for every deal.</div>
        ) : st.state === "loading" && !st.events.length ? (
          <div className="sub stnote">Syncing ShowingTime…</div>
        ) : st.state === "err" ? (
          <div className="stnote bad">{st.note}</div>
        ) : st.events.length === 0 ? (
          <div className="sub stnote">No ShowingTime activity for this address yet.</div>
        ) : (
          <div className="strows">
            {st.events.map((e, i) => {
              const { ic, cls } = stIcon(e.activity);
              return (
                <div key={i} className="strow">
                  <span className={"stico " + cls}>{ic}</span>
                  <div className="stbody">
                    <div className="stline"><b>{e.activity}</b><span className="sub">{stFmt(e.start)}</span></div>
                    {e.agent || e.brokerage ? <div className="sub">{e.agent}{e.brokerage ? " · " + e.brokerage : ""}</div> : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="sechead static">
        <span>Manual showings <span className="dim">({list.length})</span></span>
        <button className="btn slim" onClick={() => setAdding((a) => !a)}>{adding ? "Close" : "+ Add"}</button>
      </div>
      {adding ? (
        <div className="expform dispoform">
          <div className="expformgrid">
            <label><span>Buyer</span><input value={f.buyer} onChange={(e) => setF({ ...f, buyer: e.target.value })} /></label>
            <label><span>Phone</span><input value={f.ph} onChange={(e) => setF({ ...f, ph: e.target.value })} /></label>
            <label><span>Date & time</span><input type="datetime-local" value={f.dt} onChange={(e) => setF({ ...f, dt: e.target.value })} /></label>
            <label><span>Status</span><select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>{SHOWING_STATUS.map((s) => <option key={s}>{s}</option>)}</select></label>
            <label className="wide"><span>Access / notes</span><input value={f.access} onChange={(e) => setF({ ...f, access: e.target.value })} /></label>
            <label className="wide"><span>Feedback</span><input value={f.feedback} onChange={(e) => setF({ ...f, feedback: e.target.value })} /></label>
          </div>
          <div className="expformacts"><button className="btn slim primary" onClick={add}>Add</button><button className="btn slim" onClick={() => setAdding(false)}>Cancel</button></div>
        </div>
      ) : null}
      {list.length === 0 && !adding ? <div className="dispoempty">No showings yet.</div> : null}
      {list.map((s) => (
        <div key={s.id} className="dispomrow">
          <div className="dispomain">
            <div className="dispotitle">{s.buyer || "—"}</div>
            <div className="disposub">{[s.dt ? s.dt.replace("T", " ") : "", s.ph, s.feedback].filter(Boolean).join(" · ")}</div>
          </div>
          <span className="dispopill">{s.status}</span>
          <button className="rowbtn del" title="Delete" onClick={() => remove(s.id)}>✕</button>
        </div>
      ))}
    </div>
  );
}

/** Per-deal Team + chat (v1 flip Team tab). Stored on deal edits.team / edits.chat. */
function DealTeam({ deal, onSaveBlob, me }: { deal: Deal; onSaveBlob: (natKey: string, key: string, value: unknown) => void; me: string }) {
  const team = useMemo(() => narrowDealTeam(deal.fields["team"]), [deal.fields]);
  const chat = useMemo(() => narrowDealChat(deal.fields["chat"]), [deal.fields]);
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const initials = (n: string) => n.split(/\s+/).map((s) => s[0] ?? "").join("").slice(0, 2).toUpperCase() || "?";
  const assign = () => { const n = name.trim(); if (!n) return; onSaveBlob(deal.natKey, "team", [...team, { n, role: "Member" }]); setName(""); };
  const send = () => { const m = msg.trim(); if (!m) return; onSaveBlob(deal.natKey, "chat", [...chat, { who: me, m }]); setMsg(""); };
  return (
    <div className="detailsec">
      <div className="sechead static"><span>Team <span className="dim">({team.length})</span></span></div>
      <div className="relvadd">
        <input placeholder="Assign a team member…" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && assign()} />
        <button className="btn" onClick={assign}>Assign</button>
      </div>
      {team.map((m, i) => (
        <div key={i} className="wtcontact"><span className="wtavatar hue-blue">{initials(m.n)}</span><div className="wtcmain"><strong>{m.n}</strong><span className="sub">{m.role}</span></div></div>
      ))}
      <div className="wtchat">
        <div className="wtchatlog">
          {chat.length === 0 ? <div className="dispoempty">No messages yet.</div> : null}
          {chat.map((c, i) => (<div key={i} className="wtmsg"><span className="wtavatar sm hue-blue">{c.who}</span><span className="wtbubble">{c.m}</span></div>))}
        </div>
        <div className="wtchatin">
          <input placeholder="Type a message…" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
          <button className="btn slim primary" onClick={send}>Send</button>
        </div>
      </div>
    </div>
  );
}

/** Deal notes (v1 has none on the flip card — parity-fill matching the lead's
 * p.notes and wholesale's w.notes). Debounced + blur commit through the deals
 * door as edits.notes, so v1's kanban sees it on its next poll. */
function DealNotes({
  deal,
  onEditField,
}: {
  deal: Deal;
  onEditField: (natKey: string, field: string, value: string | null) => void;
}) {
  const initial = typeof deal.fields["notes"] === "string" ? (deal.fields["notes"] as string) : "";
  const [notes, setNotes] = useState(initial);
  const saved = useRef(initial);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const commit = (v: string) => {
    if (v === saved.current) return;
    saved.current = v;
    onEditField(deal.natKey, "notes", v.trim() === "" ? null : v);
  };
  const change = (v: string) => {
    setNotes(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => commit(v), 900);
  };
  // Flush on unmount rather than just cancelling — closing the drawer inside
  // the 900ms window threw away the last keystrokes, and onBlur doesn't fire
  // on Escape. Ref so the effect stays dependency-free and still sees the
  // latest value.
  const flush = useRef<() => void>(() => {});
  flush.current = () => { if (debounce.current) commit(notes); };
  useEffect(() => () => {
    if (debounce.current) clearTimeout(debounce.current);
    flush.current();
  }, []);

  return (
    <>
      <label className="lbl">Deal notes</label>
      <textarea
        className="notes"
        rows={4}
        placeholder="Notes about this deal…"
        value={notes}
        onChange={(e) => change(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
      />
    </>
  );
}

/** Related Vendors (v1 relatedVendorsSection): the deal's own vendor roster,
 * `edits.relVendors = [{name, type}]`. Whole-array replace through the deals
 * door (onSaveDealBlob). */
export function RelatedVendors({
  deal,
  contacts,
  onSaveBlob,
}: {
  deal: Deal;
  contacts: import("../contacts/api").Contact[];
  onSaveBlob: (natKey: string, key: string, value: unknown) => void;
}) {
  const vendors = useMemo(() => narrowRelVendors(deal.fields["relVendors"]), [deal.fields]);
  const contactList = useMemo(
    () => contacts.filter((c) => c.name.trim()).sort((a, b) => a.name.localeCompare(b.name)),
    [contacts],
  );
  // distinct contact types present, for the picker's type filter (v1 cType labels).
  const contactTypes = useMemo(() => {
    const s = new Set<string>();
    for (const c of contactList) if (c.type && c.type.trim()) s.add(c.type.trim());
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [contactList]);
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  // searchable + filterable "Choose from contacts" picker.
  const [pickOpen, setPickOpen] = useState(false);
  const [q, setQ] = useState("");
  const [fType, setFType] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return contactList
      .filter((c) => {
        if (fType && (c.type ?? "") !== fType) return false;
        if (!needle) return true;
        const hay = [c.name, c.company, c.type, c.phone, c.email].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(needle);
      })
      .slice(0, 60);
  }, [contactList, q, fType]);

  const save = (all: RelVendor[]) => onSaveBlob(deal.natKey, "relVendors", all);
  const add = () => {
    const n = name.trim();
    if (!n) return;
    save([...vendors, { name: n, type: type.trim() }]);
    setName("");
    setType("");
  };
  const pick = (c: import("../contacts/api").Contact) => {
    setName(c.name);
    if ((c.type ?? "").trim()) setType((c.type ?? "").trim());
    setPickOpen(false);
    setQ("");
    setFType("");
  };
  const remove = (i: number) => save(vendors.filter((_, k) => k !== i));

  return (
    <div className="detailsec open">
      <div className="sechead static">
        <span>Related vendors</span>
        {vendors.length ? <span className="sub">{vendors.length}</span> : null}
      </div>
        <div className="secbody">
          {vendors.length === 0 ? <span className="sub">No vendors linked to this deal yet.</span> : null}
          {vendors.map((v, i) => (
            <div key={i} className="relvrow">
              <div className="relvmain">
                <strong>{v.name}</strong>
                {v.type ? <span className="sub">{v.type}</span> : null}
              </div>
              <button className="rowbtn del" title="Remove vendor" onClick={() => remove(i)}>✕</button>
            </div>
          ))}
          <div className="relvadd">
            {/* v1 Add-Vendor "Choose from contacts" — search + filter the contact
                book, click a match to fill the name (and role from its type), or
                type a vendor manually below. */}
            <button
              className={"relvpickbtn" + (pickOpen ? " on" : "")}
              onClick={() => setPickOpen((v) => !v)}
              disabled={contactList.length === 0}
              title={contactList.length === 0 ? "No contacts yet" : "Search your contacts"}
            >
              {pickOpen ? "✕ Close contacts" : `👥 Choose from contacts${contactList.length ? ` (${contactList.length})` : "…"}`}
            </button>
            {pickOpen ? (
              <div className="relvpickpanel">
                <div className="relvpickbar">
                  <input
                    className="relvsearch"
                    placeholder="Search name, company, phone, email…"
                    value={q}
                    autoFocus
                    onChange={(e) => setQ(e.target.value)}
                  />
                  <select className="relvtypefilter" value={fType} onChange={(e) => setFType(e.target.value)}>
                    <option value="">All types</option>
                    {contactTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="relvpicklist">
                  {filtered.length === 0 ? (
                    <div className="sub relvpickempty">No contacts match your search.</div>
                  ) : (
                    filtered.map((c) => (
                      <button key={c.natKey} className="relvpickrow" onClick={() => pick(c)} title="Use this contact">
                        <span className="relvpickmain">
                          <strong>{c.name}</strong>
                          {c.company ? <span className="sub">{c.company}</span> : null}
                        </span>
                        {c.type ? <span className="relvpicktype">{c.type}</span> : null}
                        {c.phone ? <span className="relvpickph">{c.phone}</span> : null}
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : null}
            <input placeholder="Vendor name" value={name} onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()} />
            <input placeholder="Role (e.g. Plumber, GC)" value={type} onChange={(e) => setType(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()} />
            <button className="btn" onClick={add}>Add</button>
          </div>
        </div>
    </div>
  );
}

/** Commit-on-blur text input for deal card edits (same contract as the lead
 * drawer's TextField: trimmed, "" → null to clear). */
function DealText({ initial, onCommit }: { initial: string | null; onCommit: (v: string | null) => void }) {
  const [text, setText] = useState(initial ?? "");
  const commit = () => {
    const v = text.trim();
    if (v === (initial ?? "")) return;
    onCommit(v === "" ? null : v);
  };
  return (
    <input
      className="moneyinput"
      value={text}
      placeholder="—"
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
    />
  );
}

/** The deal's own task list — checklist (auto) tasks first, then manual ones,
 * toggled through the same tasks door the Tasks page uses. Same matching rule
 * as the lead drawer: task.deal → natKey equality. */
function DealChecklist({
  address,
  tasks,
  members = [],
  onToggleTask,
  onAddTask,
}: {
  address: string;
  tasks: TaskRec[];
  members?: string[];
  onToggleTask: (key: string, done: boolean) => void;
  onAddTask: (title: string, opts?: { deal?: string; due?: string; who?: string; pri?: string }) => void;
}) {
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [who, setWho] = useState("");
  const k = natKeyOf(address);
  // OPEN tasks on top, checked ones below (Tom 2026-07-24); auto items lead
  // within each group and the template/creation order stays stable inside.
  const mine = tasks
    .filter((t) => t.deal && natKeyOf(t.deal) === k)
    .sort((a, b) =>
      Number(isTaskDone(a.status)) - Number(isTaskDone(b.status)) ||
      Number(b.auto ?? false) - Number(a.auto ?? false));
  const openN = mine.filter((t) => !isTaskDone(t.status)).length;

  const add = () => {
    const v = title.trim();
    if (!v) return;
    const opts: { deal?: string; due?: string; who?: string } = { deal: address };
    // Stored in v1's display format ("Aug 5, 2026") — the format every other
    // due date in the app uses, and the one the server's taskDue scheduler
    // parses. Writing the raw ISO here would work in v2 and read oddly in v1.
    if (due) opts.due = fmtDateEdit(due);
    if (who.trim()) opts.who = who.trim();
    onAddTask(v, opts);
    setTitle("");
    setDue("");
    setWho("");
  };

  return (
    <>
      <label className="lbl">
        Checklist & tasks {mine.length ? <span className="sub">· {openN} open of {mine.length}</span> : null}
      </label>
      <div className="drawertasks">
        {mine.length === 0 ? <span className="sub">None yet — stage moves auto-create their checklists.</span> : null}
        {/* PHONE (Tom 2026-07-30, "this looks really bad"): the one-span row
            wrapped the checkbox onto its own line and mixed pill + title +
            full account names + date into soup. Now a fixed two-column grid —
            the box can never orphan — with the title on its own line and one
            dim meta line (⚙ auto · assignee initials · due). Done rows stay
            VISIBLE ("see all the info") but compress to one dim line each.
            Desktop keeps the original row. */}
        {mine.map((t) => {
          const done = isTaskDone(t.status);
          const initials = (t.who ?? "").split(",").map((s) => s.trim()).filter(Boolean)
            .map((s) => (s[0] ?? "").toUpperCase()).join(", ");
          const autoTitle = t.stage ? "Stage checklist: " + t.stage : "Date reminder — generated from this deal's dates";
          return (
            <div key={t.natKey} className={"taskrow slim" + (done ? " mdone" : " mopen")}>
              <input type="checkbox" checked={done} onChange={(e) => onToggleTask(t.natKey, e.target.checked)} />
              <span className={"tasktitle" + (done ? " donetext" : "")}>
                {/* desktop composition — hidden on phones by CSS */}
                <span className="ttdesk">
                  {t.auto ? (
                    <span className="lbadge boff" title={autoTitle}>auto</span>
                  ) : null} {t.title}
                  {t.due && t.due !== "—" ? <em className="tdue"> · due {t.due}</em> : null}
                  {t.who ? <em className="twho"> · {t.who}</em> : null}
                </span>
                {/* phone composition */}
                <span className="ttph">
                  <span className="ttphtitle">{t.title}{done && t.due && t.due !== "—" ? <em className="ttphdue"> · {t.due}</em> : null}</span>
                  {!done && (t.auto || initials || (t.due && t.due !== "—")) ? (
                    <span className="ttphmeta">
                      {t.auto ? <span title={autoTitle}>⚙ auto</span> : null}
                      {initials ? <span>{t.auto ? " · " : ""}{initials}</span> : null}
                      {t.due && t.due !== "—" ? <span> · due {t.due}</span> : null}
                    </span>
                  ) : null}
                </span>
              </span>
            </div>
          );
        })}
        <div className="addtask">
          <input
            placeholder="Add a task for this deal…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
          <button className="btn" onClick={add}>Add</button>
        </div>
        <div className="addtaskmeta">
          <input
            type="date"
            title="Due date (optional)"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
          <input
            list="deal-task-members"
            placeholder="Assign to (optional)"
            value={who}
            onChange={(e) => setWho(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
          <datalist id="deal-task-members">{members.map((m) => <option key={m} value={m} />)}</datalist>
        </div>
      </div>
    </>
  );
}
